/**
 * Workflow DSL Executor
 *
 * Takes a parsed DslCommand[] and executes each command by calling existing
 * action functions from designer-workflow-step-actions.ts.  Maintains an
 * alias→realId map so that @aliases used in later commands resolve to the
 * BPMN IDs returned by earlier creation commands.
 */

import { DashboardState } from "@kuika/kuika-cl-model";
import {
  type ActionCatalogHealer,
  createActionCatalogHealer,
  reloadActionDefinitions
} from "../../action-definition-refresh";
import { getValues, type ScreenDesignInstance } from "../../designer-context";
import { navigateToWorkflowView } from "../../designer-view-actions";
import {
  beginWorkflowPersistBatch,
  endWorkflowPersistBatch,
  listDesignerWorkflowSteps,
  selectDesignerWorkflowStep,
  workflowCreateUserTaskAction,
  workflowCreateSystemStepAction,
  workflowCreateIsolatedNodeAction,
  workflowUpdateUserTaskNameAction,
  workflowUpdateSystemStepNameAction,
  workflowUpdateStepPropertiesAction,
  workflowDeleteElementAction,
  workflowAddGatewayAction,
  workflowCreateGatewayPathAction,
  workflowDirectConnectAction,
  workflowSetDefaultPathAction,
  workflowInitializePathConditionAction,
  workflowConfigureMultiInstanceAction,
  workflowInitializeMiCancelConditionAction,
  workflowConfigureMiAggregationAction,
  workflowAddLaneAction,
  workflowDeleteLaneAction,
  workflowUpdateLaneNameAction,
  workflowSetLaneAssignmentAction,
  workflowAddTimerIntermediateEventAction,
  workflowSetTimerEventDueDateAction,
  workflowAttachBoundaryEventAction,
  workflowConfigureBoundaryEventAction,
  workflowAddSubprocessAction,
  workflowDeleteSubprocessAction,
  workflowUpdateSubprocessNameAction,
  workflowAddLinkedProcessAction,
  workflowDeleteLinkedProcessAction,
  workflowUpdateLinkedProcessNameAction,
  workflowConfigureLinkedProcessParametersAction,
  workflowCreateBusinessObjectParameterAction,
  workflowCreateInternalParameterAction,
  workflowUpdateProcessParameterAction,
  workflowDeleteProcessParameterAction,
  workflowAddUserInteractionAction,
  workflowSetUserTaskDueDateAction,
  workflowAddPreActionToUserTaskAction,
  workflowSetUserTaskNotificationPreferenceAction,
  workflowAddActionToSystemStepAction,
  workflowMapStepActionInputAction,
  workflowSetDynamicAssignmentAction,
  workflowMapDynamicAssignmentInputAction,
  workflowMapStepOutputParameterAction,
  workflowDeclareHappyPathPhaseAction,
  workflowAssignStepHappyPathPhaseAction,
  workflowUpdatePathNameAction,
  workflowAssignScreenToUserTaskAction,
  workflowSetScreenButtonLocationAction,
  workflowAutoLayoutAction,
  workflowEnsureStartEndEventsAction,
  workflowGetLintIssuesAction
} from "../../designer-workflow-step-actions";
import { resolveWorkflowAction } from "../../workflow-action-resolver";
import {
  canvasShrinkScope,
  checkCanvasContinuity,
  classifyCanvasRead,
  commandCreatesWitnessedNode,
  describeCanvasReadability,
  type CanvasReadability,
  type CanvasShrinkScope,
  type ICanvasContinuityVerdict,
  type ICanvasSnapshot,
  type ICanvasWitness,
  rebaseCanvasWitness
} from "../../workflow-canvas-integrity";
import type {
  WorkflowFormFieldOverride,
  WorkflowFormLayoutOverride,
  WorkflowFormSectionOverride
} from "../../workflow-form-metadata-overrides";
import {
  clearCanvasIncidents,
  describeCanvasIncidents,
  type IModelerBuildFailure,
  isBpmnModelerReady,
  MODELER_READY_TIMEOUT_MS,
  noteCanvasReadable,
  readCanvasIncidentCount,
  readModelerBuildFailure,
  recordCanvasIncident,
  waitForBpmnModelerReady
} from "../../workflow-modeler-diagnostics";
import { beginLiveLintSuspension, endLiveLintSuspension } from "./live-lint-suspension";
import { type ExecCtx, pathEdgeKey, resolve } from "./workflow-dsl-id-resolver";
import type {
  ConditionEntry,
  DslCommand,
  DslParseError,
  ScreenFieldCmd,
  ScreenFormCmd,
  ScreenSectionCmd
} from "./workflow-dsl-parser";
import {
  isScreenCompositionCommand,
  runScreenCompositionPass,
  type IScreenCompositionLineResult,
  type ScreenCompositionCommand
} from "./workflow-dsl-screen-composition";
import type { ValidationError } from "./workflow-dsl-validator";

// ── Result types ───────────────────────────────────────────────────────────

/** Minimal shape of a gateway's moddle businessObject — only the `default`
 *  sequence-flow reference we need to protect from cleanup. */
interface SequenceFlowRef {
  id?: string;
}
interface GatewayBusinessObject {
  default?: SequenceFlowRef;
}

/** Element types that are never reported as disconnected (structural, start/end,
 *  label). BoundaryEvent is intentionally NOT here — a boundary event with no
 *  outgoing flow really is an unfinished branch. bpmn:Group IS here: groups are
 *  visual artifacts with no sequence flows by design — Kuika uses marked groups as
 *  Happy Path phase columns (happyPathPhaseInterceptor), so counting them would
 *  make every process with phases look broken. */
const ORPHAN_SWEEP_EXCLUDED_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:SequenceFlow",
  "bpmn:Participant",
  "bpmn:Lane",
  "bpmn:Process",
  "bpmn:Collaboration",
  "bpmn:StartEvent",
  "bpmn:EndEvent",
  "bpmn:Group",
  "label"
]);

/**
 * Whether an element may be COUNTED as disconnected (before the has-parent /
 * no-flows checks). Excludes structural types AND any `_plane` pseudo-element: an
 * expanded/embedded SubProcess registers a DI plane `<id>_plane` (type
 * bpmn:SubProcess, no parent, no flows) that is not a node at all, so reporting it
 * would send the author chasing an element they cannot connect (KOA-17362).
 * Exported for testing.
 */
export function isOrphanSweepableElement(type: string, id: string): boolean {
  if (id.endsWith("_plane")) return false;
  return !ORPHAN_SWEEP_EXCLUDED_TYPES.has(type);
}

/**
 * Whether a sweepable element is standing OUTSIDE the flow: it is a real child
 * shape and no sequence flow enters or leaves it.
 *
 * This is character-for-character the `kuika/no-isolated-flow-nodes` lint rule,
 * and that is the point — the two must agree. What differs is what happens next:
 * the lint reports it as an error, and nothing deletes it (KOA-18136). Exported
 * for testing.
 */
export function isDisconnectedNode(hasParent: boolean, incomingFlowCount: number, outgoingFlowCount: number): boolean {
  if (!hasParent) return false;
  return incomingFlowCount === 0 && outgoingFlowCount === 0;
}

/** Commands that only read/render the diagram — they never create or wire nodes. */
const READ_ONLY_COMMAND_KINDS: ReadonlySet<string> = new Set<string>(["LIST", "LINT", "LAYOUT"]);

/**
 * Whether a script performs any structural/mutating work (anything beyond
 * LIST/LINT/LAYOUT). Used to decide whether to restore the mandatory Start/End
 * events: a build/repair script that wires to EndEvent_1 needs it to exist,
 * whereas a pure read-only inspection must leave the (possibly broken) diagram
 * untouched so the missing-End error stays visible. Exported for testing.
 */
export function scriptHasStructuralCommand(commands: readonly { kind: string }[]): boolean {
  return commands.some((c) => !READ_ONLY_COMMAND_KINDS.has(c.kind));
}

/**
 * Whether a gateway outgoing flow is an auto-generated placeholder safe to
 * remove during cleanup. A flow is removable ONLY when it has no real name
 * (empty or the bpmn "Path N" auto-name) AND it is NOT the gateway's DEFAULT
 * branch. The default branch is intentionally unnamed/condition-less, so
 * matching on name alone would delete it (KOA-17362). Exported for testing.
 */
export function isRemovableGatewayAutoFlow(
  flowId: string,
  flowName: string,
  defaultFlowId: string | undefined
): boolean {
  if (defaultFlowId !== undefined && flowId === defaultFlowId) return false;
  return flowName === "" || /^Path \d+$/.test(flowName);
}

/** Minimal shape of a gateway's outgoing flow for the dedup decision. */
export interface IGatewayFlowInfo {
  id: string;
  name: string;
  targetId: string | undefined;
}

/**
 * Whether a gateway's outgoing flow is a removable DUPLICATE auto-placeholder.
 * The name test alone (empty / "Path N") is NOT enough: users frequently rename
 * only ONE branch (e.g. "Yıllık veya Hastalık") and leave real sibling branches
 * with their default "Path N" names — those go to distinct real targets and are
 * NOT placeholders. A flow is removable only when it (a) passes the name/default
 * gate AND (b) a DIFFERENT sibling that is itself NOT a removable auto-flow
 * (a named or default branch) already targets the SAME node — i.e. it is a true
 * duplicate, the original auto-wired "Path N → END" alongside an explicit branch
 * to END. This never deletes a unique real branch (KOA-17362: "add Maternity to
 * the condition" deleted the "Path 2 → Onay bilgilendirme" branch). Exported for testing.
 */
export function isRemovableDuplicateGatewayFlow(
  flow: IGatewayFlowInfo,
  siblings: readonly IGatewayFlowInfo[],
  defaultFlowId: string | undefined
): boolean {
  if (!isRemovableGatewayAutoFlow(flow.id, flow.name, defaultFlowId)) return false;
  if (flow.targetId === undefined) return false;
  return siblings.some(
    (g) => g.id !== flow.id && g.targetId === flow.targetId && !isRemovableGatewayAutoFlow(g.id, g.name, defaultFlowId)
  );
}

/**
 * Decide which lanes to remove because they are EMPTY (no flow-node members).
 * Given the per-lane member counts (each lane's `flowNodeRef` length), return the
 * indices to delete. An empty actor lane renders as a wasted band that the
 * swimlane autoLayout sizes like any other (equal height) — so it visually
 * "interleaves" the populated lanes (KOA-17362). Rules:
 *   • never touch a pool with ≤1 lane (don't remove the only lane);
 *   • if at least one lane has members, remove EVERY empty lane;
 *   • if ALL lanes are empty (degenerate), keep the first and remove the rest —
 *     a pool must retain at least one lane.
 * Pure + side-effect-free so it is unit-testable.
 */
export function emptyLaneIndicesToRemove(memberCounts: readonly number[]): number[] {
  if (memberCounts.length <= 1) return [];
  const emptyIdx: number[] = memberCounts.map((c, i) => (c === 0 ? i : -1)).filter((i) => i >= 0);
  const nonEmptyCount: number = memberCounts.length - emptyIdx.length;
  if (nonEmptyCount >= 1) return emptyIdx;
  // All empty — keep the first lane, drop the rest.
  return emptyIdx.slice(1);
}

interface CommandResult {
  line: number;
  command: string;
  success: boolean;
  message: string;
  /** For creation commands: the real BPMN ID assigned to the alias. */
  id?: string;
  /** For query commands (LINT, LIST): full result data. */
  data?: unknown;
}

/** KOA-18448 — one command KIND's share of a script's wall time. */
export interface DslCostRow {
  kind: string;
  calls: number;
  ms: number;
}

/** KOA-18448 — the single slowest line of a script, so a cost has an address. */
export interface DslCostSlowest {
  kind: string;
  line: number;
  ms: number;
}

/**
 * KOA-18448 — WHERE A SCRIPT'S WALL TIME WENT, CARRIED BACK TO WHOEVER CAN ACT ON IT.
 *
 * The per-command ledger has existed since KOA-18136 §32 and was written only to the BROWSER
 * console — so the orchestrator, the telemetry and every person reading a slow run had the whole
 * call's duration and nothing inside it. Its own comment says why that mattered: *"before tuning
 * anything we had no per-command cost at all … so every theory about WHICH command is expensive
 * was a guess."* Live `7cf6ee92` proved the point — ninety minutes, of which fifty-six went to
 * this tool, and the breakdown was unreachable.
 *
 * Travels on the result and is STRIPPED before the author sees it (the orchestrator does that):
 * an agent told which command is expensive would optimise against the cost instead of the task,
 * and the one command it would drop first is the validation.
 */
export interface DslCostLedger {
  totalMs: number;
  settleTaxMs: number;
  slowest: DslCostSlowest;
  byKind: DslCostRow[];
}

export interface ExecutionResult {
  success: boolean;
  /**
   * False when the script executed but LINT still reports errors. The agent
   * should keep iterating (LINT → fix → LINT) until this is true.
   * Separate from `success` so LLMs don't misread lint-incomplete as a
   * command failure and retry the same command verbatim.
   */
  taskComplete: boolean;
  parseErrors: DslParseError[];
  results: CommandResult[];
  aliasMap: Record<string, string>;
  summary: string;
  /** KOA-18448 — where this script's wall time went. Stripped before the author reads it. */
  cost?: DslCostLedger;
  /** Non-fatal structural warnings from static validation. Attached by the handler. */
  validationWarnings?: ValidationError[];
  /**
   * KOA-18136 §33 — fatal structural defects found by static validation. Their
   * presence no longer stops the script: the sound commands run, these are
   * reported, and `taskComplete` is forced false so the defect cannot be read
   * as a finished build. Attached by the handler.
   */
  structuralIssues?: ValidationError[];
  /** Lines dropped before execution because building them would corrupt the diagram. */
  skippedLines?: number[];
  /**
   * True when the script executed NOTHING because a PRECONDITION was unmet — the
   * live BPMN modeler was absent (mount race, no workflow selected, or a read-only
   * lock). Set only on the atomic-abort path, where `results` carries one
   * `(modeler)` entry and the canvas was not touched at all.
   *
   * A machine-readable flag rather than prose the caller has to grep, because a
   * consumer OUTSIDE the browser depends on it: the orchestrator treats an A1 plan
   * that halts at step 0 as a planner misclassification and re-runs the turn
   * through a brief-less Designer. For this abort that inference is wrong and
   * actively harmful — nothing was misclassified, a precondition simply was not
   * met, and the brief-less Designer then attempts the very edits the planner was
   * redirected away from (live smoke chat_1785187254829_9ssyua: 20 rounds / $0.123
   * re-trying refused managed-form patches). See `handle-chat.ts` blockFallback.
   */
  preconditionUnmet?: boolean;
  /**
   * KOA-18136 §37 — the script STOPPED because the canvas it was building on stopped
   * being the canvas it started on. Present only then. Nothing was cleaned and nothing
   * was saved after this point, and the turn may not report completion: the orchestrator
   * reads this field to refuse "built" and to hold back the screen-assignment and the
   * Designer passes that would otherwise run on a half-built process.
   */
  canvasHalt?: ICanvasHalt;
}

/** Where and how a script was stopped by a canvas failure (KOA-18136 §37). */
export interface ICanvasHalt {
  /** Script line that was NOT executed. */
  readonly line: number;
  /** Its command kind. */
  readonly command: string;
  /** `unreadable` | `replaced` | `lost`. */
  readonly continuity: string;
  /** Nodes this script created that the canvas no longer has. */
  readonly missingIds: readonly string[];
  /** Commands that ran before the stop. */
  readonly appliedCommands: number;
  /** Commands the script contained. */
  readonly totalCommands: number;
}

// ── Alias resolution ───────────────────────────────────────────────────────
// `ExecCtx` + `resolve` now live in ./workflow-dsl-id-resolver so the pure
// ref-validation logic can be unit-tested without bpmn.js / window.

/**
 * Map the well-known BPMN anchors (`StartEvent_1` / `EndEvent_1`) to the diagram's real
 * start and end elements. LLMs assume the conventional names; real diagrams rename them.
 *
 * KOA-18136 §37 — this is also a REPAIR, not only a preload, and it is called again
 * before every graph command. The mapping used to be taken once at the first line and
 * kept for the whole script, so an anchor whose element stopped existing mid-run
 * resolved to a dead id and every line using it failed "Source or Target node not found
 * in diagram" — live session be14b8f5, the four rejection-branch `CONNECT … ->
 * EndEvent_1` lines, i.e. the entire reject path of the process, dropped that way.
 *
 * An anchor whose current mapping IS present is left untouched: re-pointing a live
 * anchor at a different element would silently rewire the author's graph.
 */
export function preloadWellKnownIds(aliasMap: Record<string, string>): void {
  try {
    const modeler = (window as unknown as { bpmnModeler?: { get: (name: string) => unknown } }).bpmnModeler;
    if (!modeler || typeof modeler.get !== "function") return;
    const elementRegistry = modeler.get("elementRegistry") as {
      filter: (fn: (e: { type: string; id: string }) => boolean) => { id: string }[];
      get: (id: string) => unknown;
    };
    if (!elementRegistry) return;

    const anchor = (name: string, bpmnType: string): void => {
      const current: string = aliasMap[name] ?? name;
      // Live mapping — leave it exactly as it is.
      if (elementRegistry.get && elementRegistry.get(current)) return;
      const candidates: { id: string }[] = elementRegistry.filter(
        (e: { type: string; id: string }): boolean => e.type === bpmnType
      );
      const realId: string | undefined = candidates.length > 0 ? candidates[0].id : undefined;
      if (realId && realId !== name) aliasMap[name] = realId;
    };

    anchor("StartEvent_1", "bpmn:StartEvent");
    anchor("EndEvent_1", "bpmn:EndEvent");
  } catch {
    // Silently ignore — bpmn modeler may not be ready
  }
}

/** One node the cleanup found standing outside the flow. */
interface DisconnectedNode {
  readonly id: string;
  readonly type: string;
  readonly name: string;
}

/**
 * Final pass before LINT/LAYOUT. It repairs the ARROWS that bpmn.js's async
 * auto-wiring leaves behind when it races our explicit connection management, and
 * it REPORTS the nodes that ended up outside the flow.
 *
 * ── KOA-18136: why it no longer deletes a node ───────────────────────────────
 *
 * It used to remove any node with no incoming and no outgoing sequence flow. That
 * predicate is character-for-character the `kuika/no-isolated-flow-nodes` lint
 * rule, which is configured as an ERROR — so the two disagreed about what a
 * disconnected node means, and the destructive one ran first: this function is the
 * LINT command's opening act. Live `16987e7e` shows the whole chain — six branch
 * lines were refused, two authored steps were left edgeless, this sweep deleted
 * them, and the lint that would have named them then found a clean graph and let
 * the build finish one declared user step short. The process shipped without the
 * EHS review that the specification asked for, and the run summary said the fixes
 * had SUCCEEDED.
 *
 * A node with no arrows is an unfinished drawing, and the repair is to draw the
 * arrow. Deleting it destroys the only evidence that anything is missing, and the
 * evidence is precisely what the lint, the read-back gate and the author's own
 * repair loop all run on. So the sweep now COUNTS what it used to remove and hands
 * the list back; the deletion belongs to the author, through an explicit DELETE.
 *
 * Arrow cleanup (steps 1, 3, 4) and empty-lane removal (step 5) stay: a duplicate
 * auto-flow and an actor lane with no steps in it are artifacts of the drawing
 * machinery, not work anybody authored.
 */
function finalCleanup(phase = "unknown"): DisconnectedNode[] {
  try {
    const modeler = (window as unknown as { bpmnModeler?: { get: (name: string) => unknown } }).bpmnModeler;
    if (!modeler || typeof modeler.get !== "function") return [];
    const elementRegistry = modeler.get("elementRegistry") as {
      filter: (
        fn: (e: {
          type: string;
          id: string;
          businessObject?: { name?: string };
          incoming?: { type: string }[];
          outgoing?: { type: string }[];
        }) => boolean
      ) => {
        id: string;
        type: string;
        incoming?: {
          type: string;
        }[];
        outgoing?: {
          type: string;
          id: string;
          businessObject?: { name?: string };
          target?: { type: string; id?: string };
        }[];
        businessObject?: { name?: string };
        children?: { type: string }[];
      }[];
    };
    const modeling = modeler.get("modeling") as {
      removeConnection: (conn: unknown) => void;
      removeElements: (elems: unknown[]) => void;
    };
    if (!elementRegistry || !modeling) return [];

    console.log(`[DSL-CLEANUP ${phase}] --- finalCleanup START ---`);

    // 1. Clean up DUPLICATE "Path N" auto-generated flows on gateways. Only a
    //    "Path N"/unnamed flow that duplicates a real sibling's target is a
    //    placeholder; a "Path N" flow to a UNIQUE target is a real branch the
    //    user simply never renamed and must be kept (KOA-17362).
    const gateways = elementRegistry.filter((e) => e.type.includes("Gateway"));
    for (const gw of gateways) {
      if (!gw.outgoing || gw.outgoing.length <= 1) continue;

      // The gateway's DEFAULT branch is intentionally UNNAMED — it carries no
      // WHEN condition and no label by design. `workflowSetDefaultPathAction`
      // sets `gateway.businessObject.default` to the default flow's businessObject.
      const defaultFlowId: string | undefined = (gw.businessObject as unknown as GatewayBusinessObject | undefined)
        ?.default?.id;

      const flows = gw.outgoing.filter((c) => c.type === "bpmn:SequenceFlow");
      const siblings: IGatewayFlowInfo[] = flows.map((f) => ({
        id: f.id,
        name: f.businessObject?.name || "",
        targetId: f.target?.id
      }));
      const autoFlows = flows.filter((f) =>
        isRemovableDuplicateGatewayFlow(
          { id: f.id, name: f.businessObject?.name || "", targetId: f.target?.id },
          siblings,
          defaultFlowId
        )
      );
      for (const autoFlow of autoFlows) {
        console.log(
          `[DSL-CLEANUP ${phase}] step1 remove DUPLICATE auto-flow ${autoFlow.id} (name="${autoFlow.businessObject?.name || ""}") from gateway ${gw.id} -> target ${autoFlow.target?.id}`
        );
        modeling.removeConnection(autoFlow);
      }
    }

    // 2. OBSERVE the nodes standing outside the flow (no incoming AND no outgoing
    //    sequence flow, excluding Start/End/Lane/Pool). Nothing is removed here —
    //    see the header: this is the evidence that a step was declared and never
    //    wired, and it is returned so the run says so out loud.
    //
    //    Note the empty template's "Sample Step" arrives WIRED (Start → Sample Step
    //    → End, `buildDefaultWorkflowXml`), so it appears in this list only when a
    //    script rewired around it and left it dangling — which is a real unfinished
    //    edit, reported like any other.
    const readDisconnected = (): DisconnectedNode[] => {
      const sweepable = elementRegistry.filter((e) => isOrphanSweepableElement(e.type, e.id)) as {
        id: string;
        type: string;
        businessObject?: { name?: string };
        parent?: unknown;
        incoming?: { type: string }[];
        outgoing?: { type: string }[];
      }[];
      return sweepable
        .filter((e) =>
          isDisconnectedNode(
            !!e.parent,
            (e.incoming || []).filter((c) => c.type === "bpmn:SequenceFlow").length,
            (e.outgoing || []).filter((c) => c.type === "bpmn:SequenceFlow").length
          )
        )
        .map((e): DisconnectedNode => ({ id: e.id, type: e.type, name: e.businessObject?.name || "" }));
    };
    for (const node of readDisconnected()) {
      console.warn(
        `[DSL-CLEANUP ${phase}] step2 DISCONNECTED ${node.id} (type=${node.type}, name="${node.name}") — reported, NOT removed`
      );
    }

    // 3. Remove duplicate connections from gateways (same source → same target)
    const gateways2 = elementRegistry.filter((e) => e.type.includes("Gateway"));
    for (const gw of gateways2) {
      const flows2 = (gw.outgoing || []).filter((c) => c.type === "bpmn:SequenceFlow") as {
        id: string;
        type: string;
        businessObject?: { name?: string };
        target?: { id: string };
      }[];
      const seen = new Map<string, (typeof flows2)[0]>();
      for (const flow of flows2) {
        const targetId = flow.target?.id;
        if (!targetId) continue;
        if (seen.has(targetId)) {
          const existing = seen.get(targetId)!;
          const existingName = existing.businessObject?.name || "";
          const currentName = flow.businessObject?.name || "";
          // Keep the named one, remove the unnamed
          if (currentName && !existingName) {
            console.log(
              `[DSL-CLEANUP ${phase}] step3 remove DUPLICATE ${existing.id} (name="${existingName}") from gateway ${gw.id} -> target ${targetId}; keeping ${flow.id} (name="${currentName}")`
            );
            modeling.removeConnection(existing);
            seen.set(targetId, flow);
          } else if (!currentName) {
            console.log(
              `[DSL-CLEANUP ${phase}] step3 remove DUPLICATE ${flow.id} (name="${currentName}") from gateway ${gw.id} -> target ${targetId}; keeping ${existing.id} (name="${existingName}")`
            );
            modeling.removeConnection(flow);
          } else {
            console.log(
              `[DSL-CLEANUP ${phase}] step3 NOTE: two named flows to same target from gateway ${gw.id} -> target ${targetId}: ${existing.id}("${existingName}") vs ${flow.id}("${currentName}") — kept both`
            );
          }
        } else {
          seen.set(targetId, flow);
        }
      }
    }

    // 4. Remove duplicate outgoing flows (same source → same target) on
    //    NON-gateway nodes. A task / event / start node can only ever have
    //    one sequence flow to a given target; a duplicate appears when a
    //    node's `after:` auto-wiring (deferred via setTimeout) creates
    //    source→End and an explicit `CONNECT source -> EndEvent_1` runs
    //    before workflowDirectConnectAction's idempotency check can observe
    //    the not-yet-flushed auto-wired edge — producing two arrows to End.
    //    Gateways are intentionally skipped: step 3 owns them and preserves
    //    the named-flow-vs-auto-flow distinction needed for branch routing.
    const nonGatewaySources = elementRegistry.filter(
      (e) =>
        !e.type.includes("Gateway") &&
        e.type !== "bpmn:SequenceFlow" &&
        e.type !== "bpmn:Participant" &&
        e.type !== "bpmn:Lane" &&
        e.type !== "bpmn:Process" &&
        e.type !== "bpmn:Collaboration" &&
        e.type !== "label"
    );
    for (const node of nonGatewaySources) {
      const flows = (node.outgoing || []).filter((c) => c.type === "bpmn:SequenceFlow");
      if (flows.length <= 1) continue;
      const seenTarget = new Map<string, (typeof flows)[0]>();
      for (const flow of flows) {
        const targetId = flow.target?.id;
        if (!targetId) continue;
        const prior = seenTarget.get(targetId);
        if (!prior) {
          seenTarget.set(targetId, flow);
          continue;
        }
        // Duplicate edge to the same target — keep a named flow over an
        // unnamed one, otherwise keep the first seen.
        const priorNamed = (prior.businessObject?.name || "") !== "";
        const flowNamed = (flow.businessObject?.name || "") !== "";
        const drop = flowNamed && !priorNamed ? prior : flow;
        const keep = drop === prior ? flow : prior;
        console.log(
          `[DSL-CLEANUP ${phase}] step4 remove DUPLICATE ${drop.id} from ${node.id} -> target ${targetId}; keeping ${keep.id}`
        );
        modeling.removeConnection(drop);
        seenTarget.set(targetId, keep);
      }
    }

    // 5. Remove EMPTY lanes — an actor lane the builder created but never placed
    //    a step in. The swimlane autoLayout sizes every lane to an equal band, so
    //    an empty lane shows as a wasted strip that "interleaves" the populated
    //    lanes (KOA-17362). Runs pre-lint (before LAYOUT) so the layout never
    //    sizes the dead band. `flowNodeRef` is the bpmn lane membership the
    //    autoLayout's nodeToPartition reads; keep at least one lane.
    const lanes = elementRegistry.filter((e) => e.type === "bpmn:Lane") as {
      id: string;
      businessObject?: { name?: string; flowNodeRef?: unknown[]; get?: (k: string) => unknown };
    }[];
    if (lanes.length > 1) {
      const memberCounts: number[] = lanes.map((l) => {
        const refs: unknown = l.businessObject?.get
          ? l.businessObject.get("flowNodeRef")
          : l.businessObject?.flowNodeRef;
        return Array.isArray(refs) ? refs.length : 0;
      });
      for (const idx of emptyLaneIndicesToRemove(memberCounts)) {
        const lane = lanes[idx];
        console.log(
          `[DSL-CLEANUP ${phase}] step5 remove EMPTY lane ${lane.id} (name="${lane.businessObject?.name || ""}")`
        );
        try {
          modeling.removeElements([lane]);
        } catch (err) {
          console.warn(`[DSL-CLEANUP ${phase}] step5 failed to remove empty lane ${lane.id}:`, err);
        }
      }
    }

    console.log(`[DSL-CLEANUP ${phase}] --- finalCleanup END ---`);
    // Re-read AFTER the arrow cleanup: steps 3 and 4 remove duplicate flows, and a
    // node whose only arrow was a removed duplicate has just become disconnected.
    // Reporting the pre-cleanup list would name the wrong set.
    return readDisconnected();
  } catch (e) {
    console.error(`[DSL-CLEANUP ${phase}] threw:`, e);
    return [];
  }
}

/**
 * Extract a BPMN ID from an action result message.
 * Messages follow patterns like:
 *   "User task created with ID: Activity_18e0ykl"
 *   "Subprocess created: Activity_xyz"
 *   "Lane created with ID: Lane_03jw7ce"
 *   "Path handled with ID: Flow_123"
 *
 * IMPORTANT: "with ID:" must be tried before bare "created:" because
 * messages like "created with ID:" would otherwise match "created" and
 * capture "with" as the ID.
 */
function extractIdFromResult(result: { message?: string; laneId?: string }): string | undefined {
  if (result.laneId) return result.laneId;
  if (!result.message) return undefined;

  // Try "with ID:" first — this is the most common pattern
  const withIdMatch = result.message.match(/with ID:\s*(\S+)/i);
  if (withIdMatch) return withIdMatch[1];

  // Fall back to "created: <id>" (no "with ID" in between)
  const createdMatch = result.message.match(/created:\s*(\S+)/i);
  if (createdMatch) return createdMatch[1];

  return undefined;
}

// ── Executor ───────────────────────────────────────────────────────────────

/**
 * Remove all auto-generated outgoing flows from a gateway.
 * insertBpmnNode creates a "Path 1" outgoing flow when inserting a gateway
 * between two nodes. In DSL mode, all gateway outgoing flows should come
 * from explicit PATH commands, not auto-wiring.
 */
function cleanupGatewayAutoFlows(gatewayId: string): void {
  try {
    const modeler = (window as unknown as { bpmnModeler?: { get: (name: string) => unknown } }).bpmnModeler;
    if (!modeler || typeof modeler.get !== "function") return;
    const elementRegistry = modeler.get("elementRegistry") as {
      get: (id: string) => { outgoing?: { type: string; id: string }[] } | undefined;
    };
    const modeling = modeler.get("modeling") as { removeConnection: (conn: unknown) => void };
    if (!elementRegistry || !modeling) return;

    const gateway = elementRegistry.get(gatewayId);
    if (!gateway || !gateway.outgoing) return;

    // Remove all outgoing SequenceFlows — PATH commands will recreate the intended ones
    const outgoingFlows = gateway.outgoing.filter((c) => c.type === "bpmn:SequenceFlow");
    for (const flow of outgoingFlows) {
      modeling.removeConnection(flow);
    }
  } catch {
    // Silently ignore
  }
}

/**
 * DSL command kinds that never read or mutate the DIAGRAM — they go through the
 * workflow provider, the project model, or backend services. A script made up
 * entirely of these needs no modeler, so it must NOT wait for one (it can run with
 * no workflow canvas open at all). Every other kind touches the diagram and
 * therefore requires a live modeler.
 *
 * Membership is a claim about the IMPLEMENTATION, not about how "graph-ish" a
 * command sounds. Verify against the action a kind dispatches to before adding one.
 */
const MODELER_FREE_COMMAND_KINDS: ReadonlySet<string> = new Set<string>([
  "PARAM_BO",
  "PARAM_INTERNAL",
  "UPDATE_PARAM",
  "DELETE_PARAM",
  // KOA-17362 — SCREEN_FIELD is a pure DECLARATION: it reads and mutates nothing,
  // it only records task-screen field config that ASSIGN_SCREEN consumes.
  "SCREEN_FIELD",
  "SCREEN_SECTION",
  "SCREEN_FORM",
  "SCREEN_BUTTONS",
  // ASSIGN_SCREEN does not touch the diagram either — verified against
  // `workflowAssignScreenToUserTaskAction` (designer-workflow-step-actions.ts
  // 3240-3584, zero modeler references): it resolves the workflow from
  // `sd.workFlowList`, the step from that workflow MODEL via
  // `findAllStepsRecursively`, the managed link from `project.processStepScreenIds`,
  // and does its real work through backend services (`GetFormMetadataFromWorkflowBo`
  // / `GetFormMetadataOfScreen` / `UpdateWorkflowScreens`).
  //
  // Listing it here is what lets a user RE-CONFIGURE the task screen they are
  // looking at without being thrown into the BPMN editor first. It used to require
  // the modeler purely by omission, and that accident had a real cost: from a task
  // screen no workflow is open, so the script aborted, and the only "fix" available
  // to the planner was to navigate the user away from the screen they had just asked
  // about — which the tool docs separately forbid (React leaks / UI hang) and which
  // no user asked for. A build script that creates the graph still waits, because
  // its TASK / GATEWAY / CONNECT lines are not in this set.
  "ASSIGN_SCREEN",
  // KOA-17362 Adım 3 — `PHASE` only appends to `wf.happyPathDefinition` and saves;
  // it never reads or mutates the diagram. `PHASE_ASSIGN` is NOT listed: it resolves
  // a step through `forceSyncAndFindNode`, exactly like MAP_INPUT / MAP_OUTPUT, so it
  // keeps their modeler requirement.
  "PHASE"
]);

/**
 * KOA-17362 §7.6 Eksen 1 — the script's task-screen configuration (fields, titled
 * sections, the default section's layout), collected up front so ASSIGN_SCREEN can
 * apply it no matter where those lines sit. `consumedLines` records which lines an
 * ASSIGN_SCREEN actually took, so config that targets no assigned step is reported
 * instead of silently vanishing.
 */
export interface ScreenFieldPlan {
  readonly configs: readonly ScreenFieldCmd[];
  readonly sections: readonly ScreenSectionCmd[];
  readonly forms: readonly ScreenFormCmd[];
  readonly consumedLines: Set<number>;
}

/** One assigned step's screen config, in the shape the assign action consumes. */
export interface StepFieldConfig {
  readonly stepId: string;
  readonly fields: readonly WorkflowFormFieldOverride[];
  readonly sections?: readonly WorkflowFormSectionOverride[];
  readonly form?: WorkflowFormLayoutOverride;
}

/**
 * Pair each SCREEN_FIELD line with the assigned step it targets. Both sides are
 * resolved through `resolve` so an `@alias` and the literal BPMN id it maps to are
 * the same step. A ref that cannot be resolved (stale alias) simply matches nothing
 * — it is then reported as unapplied, never applied to the wrong screen.
 */
export function collectStepFieldConfigs(
  plan: ScreenFieldPlan,
  resolvedStepIds: readonly string[],
  ctx: ExecCtx
): StepFieldConfig[] {
  /** The step this line targets, when the ASSIGN_SCREEN covers it. */
  const targetStepOf = (stepRef: string): string | undefined => {
    let target: string;
    try {
      target = resolve(stepRef, ctx.aliasMap, ctx);
    } catch {
      return undefined;
    }
    return resolvedStepIds.includes(target) ? target : undefined;
  };

  const fieldsByStep: Map<string, WorkflowFormFieldOverride[]> = new Map<string, WorkflowFormFieldOverride[]>();
  const sectionsByStep: Map<string, WorkflowFormSectionOverride[]> = new Map<string, WorkflowFormSectionOverride[]>();
  const formByStep: Map<string, WorkflowFormLayoutOverride> = new Map<string, WorkflowFormLayoutOverride>();

  for (const config of plan.configs) {
    const target: string | undefined = targetStepOf(config.stepId);
    if (target === undefined) continue;
    const override: WorkflowFormFieldOverride = {
      param: config.param,
      ...(config.hidden !== undefined ? { hidden: config.hidden } : {}),
      ...(config.isReadonly !== undefined ? { isReadonly: config.isReadonly } : {}),
      ...(config.isRequired !== undefined ? { isRequired: config.isRequired } : {}),
      ...(config.label !== undefined ? { label: config.label } : {}),
      ...(config.order !== undefined ? { order: config.order } : {}),
      ...(config.element !== undefined ? { element: config.element } : {}),
      ...(config.scale !== undefined ? { scale: config.scale } : {}),
      ...(config.section !== undefined ? { section: config.section } : {})
    };
    const existing: WorkflowFormFieldOverride[] = fieldsByStep.get(target) ?? [];
    existing.push(override);
    fieldsByStep.set(target, existing);
    plan.consumedLines.add(config.line);
  }

  for (const section of plan.sections) {
    const target: string | undefined = targetStepOf(section.stepId);
    if (target === undefined) continue;
    const override: WorkflowFormSectionOverride = {
      title: section.title,
      ...(section.columns !== undefined ? { columns: section.columns } : {}),
      ...(section.tablet !== undefined ? { tablet: section.tablet } : {}),
      ...(section.mobile !== undefined ? { mobile: section.mobile } : {})
    };
    const existing: WorkflowFormSectionOverride[] = sectionsByStep.get(target) ?? [];
    existing.push(override);
    sectionsByStep.set(target, existing);
    plan.consumedLines.add(section.line);
  }

  for (const form of plan.forms) {
    const target: string | undefined = targetStepOf(form.stepId);
    if (target === undefined) continue;
    // Later line wins — one form has one default-section layout.
    formByStep.set(target, {
      ...(form.columns !== undefined ? { columns: form.columns } : {}),
      ...(form.tablet !== undefined ? { tablet: form.tablet } : {}),
      ...(form.mobile !== undefined ? { mobile: form.mobile } : {})
    });
    plan.consumedLines.add(form.line);
  }

  const stepIds: string[] = Array.from(
    new Set<string>([...fieldsByStep.keys(), ...sectionsByStep.keys(), ...formByStep.keys()])
  );
  return stepIds.map((stepId: string): StepFieldConfig => {
    const sections: WorkflowFormSectionOverride[] | undefined = sectionsByStep.get(stepId);
    const form: WorkflowFormLayoutOverride | undefined = formByStep.get(stepId);
    return {
      stepId,
      fields: fieldsByStep.get(stepId) ?? [],
      ...(sections && sections.length > 0 ? { sections } : {}),
      ...(form ? { form } : {})
    };
  });
}

/**
 * True when a DSL script has at least one command that needs the live BPMN
 * modeler (anything that reads or mutates the diagram). A script made up entirely
 * of {@link MODELER_FREE_COMMAND_KINDS} returns false so it never waits for a
 * modeler that may not exist. Exported for testing.
 */
export function scriptNeedsModeler(commands: readonly { kind: string }[]): boolean {
  return commands.some((c: { kind: string }): boolean => !MODELER_FREE_COMMAND_KINDS.has(c.kind));
}

/** True when THIS command reads or mutates the diagram (KOA-18136 §37). */
export function commandNeedsModeler(kind: string): boolean {
  return !MODELER_FREE_COMMAND_KINDS.has(kind);
}

/**
 * KOA-18136 §37 — one look at the live canvas: can it be read, which diagram is it,
 * and what is on it.
 *
 * Everything the continuity law needs comes from a single registry read, so a check
 * before every graph command costs one `getAll()` (tens of elements) against commands
 * that each cost 100ms+.
 */
function probeCanvas(): ICanvasSnapshot {
  try {
    const modeler = (window as unknown as { bpmnModeler?: { get: (name: string) => unknown } }).bpmnModeler;
    if (!modeler || typeof modeler.get !== "function") {
      return { readability: "modeler-absent", rootId: "", presentIds: new Set<string>() };
    }
    const elementRegistry = modeler.get("elementRegistry") as
      | { getAll: () => { id?: string; type?: string }[] }
      | undefined;
    const all: { id?: string; type?: string }[] = elementRegistry?.getAll ? elementRegistry.getAll() : [];
    const readability: CanvasReadability = classifyCanvasRead({
      modelerPresent: true,
      elementCount: all.length
    });
    const presentIds: Set<string> = new Set<string>();
    for (const element of all) {
      if (element?.id) presentIds.add(String(element.id));
    }
    return { readability, rootId: canvasRootId(modeler, all), presentIds };
  } catch {
    // A registry that throws is not a readable canvas.
    return { readability: "modeler-absent", rootId: "", presentIds: new Set<string>() };
  }
}

/**
 * Identity of the mounted diagram. `canvas.getRootElement()` is the authority; the
 * registry scan is the fallback for a canvas module that is not available.
 */
function canvasRootId(modeler: { get: (name: string) => unknown }, all: { id?: string; type?: string }[]): string {
  try {
    const canvas = modeler.get("canvas") as { getRootElement?: () => { id?: string } } | undefined;
    const rootId: string | undefined = canvas?.getRootElement?.()?.id;
    if (rootId) return String(rootId);
  } catch {
    /* fall through to the registry scan */
  }
  const root = all.find(
    (element: { type?: string }): boolean => element?.type === "bpmn:Process" || element?.type === "bpmn:Collaboration"
  );
  return root?.id ? String(root.id) : "";
}

/**
 * Deterministic execution ordering for a parsed DSL script.
 *
 * Three order dependencies the LLM cannot be trusted to satisfy by hand:
 *
 * 1. (F3) PARAM declarations first. `MAP_OUTPUT` / `CONDITION` / `WHEN` resolve a
 *    param by NAME and hard-fail ("Parameter '<x>' not found — create it first")
 *    if their `PARAM INTERNAL` / `PARAM BO` line comes later. Param declarations
 *    have no prerequisites, so hoisting them is always safe.
 *
 * 2. (KOA-17362) `INTERACTION` after its `MAP_OUTPUT`. `INTERACTION <ref> SET
 *    <p>=<v>` writes a FIXED value into a task OUTPUT param that `MAP_OUTPUT`
 *    declares; `buildInteractionParameters` HARD-FAILS ("INTERACTION SET target
 *    '<p>' is not an output parameter of this task. MAP_OUTPUT it first.") when the
 *    INTERACTION line precedes its MAP_OUTPUT, because `step.outputParameters` is
 *    only populated once MAP_OUTPUT has run — and the LLM could NOT self-repair the
 *    ordering across retries (observed: 4 failed retries, the approve/reject
 *    decision buttons never attached). INTERACTION is a LEAF (nothing else reads
 *    its result), so deferring every INTERACTION to run after all non-terminal
 *    commands — but BEFORE the terminal LINT/LAYOUT so validation/layout still see
 *    the final state — guarantees the MAP_OUTPUTs ran first.
 *
 * 3. AN EDGE IS WIRED AFTER BOTH ITS ENDS EXIST. `PATH` / `CONNECT` resolve each
 *    `@alias` against the map the run fills AS IT GOES, so an edge naming a node the
 *    same script creates FURTHER DOWN is refused ("Unresolved alias"). Writing a
 *    gateway's branches next to the gateway and the branch targets below it is the
 *    natural way to author a process — and the static pre-check accepts it, because
 *    its dangling-ref rule asks whether the alias is declared ANYWHERE in the script,
 *    not whether it is declared earlier. So the two disagreed, and the script that
 *    passed the check could not run.
 *
 *    Live `16987e7e`: six branch lines were refused this way. Two nodes therefore
 *    ended the script with no edge at all — the EHS review task and the special-work
 *    gateway — the post-execution cleanup swept them as orphans, and the process
 *    shipped with four of its five declared user steps. The branches that DID land
 *    kept their label and condition; the ones rebuilt afterwards by raw `CONNECT`
 *    could carry neither, which is why every "approved" path came out unnamed.
 *
 *    Fixed by DEFERRING the edges rather than hoisting the creations: hoisting would
 *    move a creation above the `LIST` that seeds the stale-id check (and above the
 *    param bucket's own ordering), while deferring only ever turns a failing resolve
 *    into a succeeding one. `DEFAULT` / `CONDITION` address a path by the id a `PATH`
 *    produced, so they defer WITH the edges and keep their relative order.
 *
 *    The deferral stops at an explicit DELETE. A script that deletes a node and then
 *    re-creates it under the same name is ordinary (the skeleton's placeholder is
 *    removed exactly that way), and an edge that floated across the delete would
 *    either wire a node that is about to vanish or wire the wrong one — so each
 *    delete closes the segment it sits in and edges only move to the end of their own.
 *
 * Every bucket filter is stable (preserves relative order), so a WELL-FORMED
 * script (params first, edges after the nodes they join, MAP_OUTPUT before
 * INTERACTION, LINT/LAYOUT last) is reordered to a BYTE-IDENTICAL sequence — the
 * reorder only bites the mis-ordered case. `results` is re-sorted to input-line
 * order after the loop, so the reported order is unchanged regardless.
 */
/**
 * Commands that WIRE existing nodes rather than create them — deferred to the end of
 * their segment so both ends of every edge exist by the time it runs.
 *
 * `DEFAULT` and `CONDITION` are here because they address a path by the id a `PATH`
 * produced; they must travel with the edges, not ahead of them.
 */
const EDGE_COMMAND_KINDS: ReadonlySet<string> = new Set<string>(["PATH", "CONNECT", "DEFAULT", "CONDITION"]);

/**
 * Commands an edge must never float past. Two different reasons, one mechanism:
 *
 *  - the deletes, because a script may remove a node and re-declare one under the
 *    same alias; an edge carried across the delete would join the wrong node, or one
 *    that is about to vanish;
 *  - LINT and LAYOUT, because they are the script's judgement and its arrangement.
 *    Deferring an edge past the validation would hand the check a graph the script
 *    had not finished drawing — the exact failure this ordering exists to prevent.
 *
 * Each of these closes the segment it sits in, so the deferral only ever reorders
 * work that stands between two barriers.
 */
const EDGE_BARRIER_COMMAND_KINDS: ReadonlySet<string> = new Set<string>([
  "DELETE",
  "LANE_DELETE",
  "SUBPROCESS_DELETE",
  "LINKED_DELETE",
  "LINT",
  "LAYOUT"
]);

/**
 * Move every edge command to the end of its own barrier-delimited segment, keeping
 * both groups in their authored order.
 *
 * Stable twice over: within the non-edge group and within the edge group. A script
 * that already declares its nodes before the edges that join them comes back
 * unchanged.
 */
function deferEdgeCommands(commands: readonly DslCommand[]): DslCommand[] {
  const out: DslCommand[] = [];
  let segment: DslCommand[] = [];
  const flushSegment = (): void => {
    if (segment.length === 0) return;
    out.push(...segment.filter((c: DslCommand): boolean => !EDGE_COMMAND_KINDS.has(c.kind)));
    out.push(...segment.filter((c: DslCommand): boolean => EDGE_COMMAND_KINDS.has(c.kind)));
    segment = [];
  };
  for (const cmd of commands) {
    if (EDGE_BARRIER_COMMAND_KINDS.has(cmd.kind)) {
      flushSegment();
      out.push(cmd);
      continue;
    }
    segment.push(cmd);
  }
  flushSegment();
  return out;
}

export function orderDslCommandsForExecution(commands: DslCommand[]): DslCommand[] {
  // KOA-17362 §7.6 slice 4 — SCREEN_BUTTONS writes a PROJECT setting the backend reads
  // when it generates each managed screen, so it must be persisted before ASSIGN_SCREEN
  // runs. It has no prerequisites of its own, so it hoists with the param declarations.
  const paramKinds: Set<DslCommand["kind"]> = new Set<DslCommand["kind"]>([
    "PARAM_INTERNAL",
    "PARAM_BO",
    "SCREEN_BUTTONS"
  ]);
  const interactionKinds: Set<DslCommand["kind"]> = new Set<DslCommand["kind"]>(["INTERACTION"]);
  const terminalKinds: Set<DslCommand["kind"]> = new Set<DslCommand["kind"]>(["LINT", "LAYOUT"]);

  const hasInteraction: boolean = commands.some((c: DslCommand): boolean => interactionKinds.has(c.kind));
  if (hasInteraction) {
    // 4-bucket: params → graph + MAP_OUTPUT + actions → INTERACTION → LINT/LAYOUT.
    return [
      ...commands.filter((c: DslCommand): boolean => paramKinds.has(c.kind)),
      ...deferEdgeCommands(
        commands.filter(
          (c: DslCommand): boolean =>
            !paramKinds.has(c.kind) && !interactionKinds.has(c.kind) && !terminalKinds.has(c.kind)
        )
      ),
      ...commands.filter((c: DslCommand): boolean => interactionKinds.has(c.kind)),
      ...commands.filter((c: DslCommand): boolean => terminalKinds.has(c.kind))
    ];
  }

  // No INTERACTION — preserve the committed F3 param-only hoist EXACTLY (2-bucket),
  // with the edge deferral applied inside the non-param bucket.
  const hasParamCmd: boolean = commands.some((c: DslCommand): boolean => paramKinds.has(c.kind));
  const hasNonParamCmd: boolean = commands.some((c: DslCommand): boolean => !paramKinds.has(c.kind));
  if (hasParamCmd && hasNonParamCmd) {
    return [
      ...commands.filter((c: DslCommand): boolean => paramKinds.has(c.kind)),
      ...deferEdgeCommands(commands.filter((c: DslCommand): boolean => !paramKinds.has(c.kind)))
    ];
  }
  return deferEdgeCommands(commands);
}

/**
 * Explain WHY the modeler is absent, so the abort names a cause instead of
 * always blaming a mount race (KOA-17362).
 *
 * "Still mounting — retry unchanged" is only true for a transient race. Two other
 * conditions look identical to `isBpmnModelerReady()` but are PERMANENT, and
 * retrying an identical script against them burns the whole turn:
 *  - NO WORKFLOW SELECTED — the canvas has nothing to build; a re-send can never
 *    succeed until something opens a workflow.
 *  - READ-ONLY — the workflow is locked by another user, so WorkFlowWindow built a
 *    NavigatedViewer (`window.viewer`), which never sets `window.bpmnModeler`.
 *    Editing is impossible, not late.
 * Cheap window/state reads only; never throws.
 */
export function describeModelerAbsence(sdInstance?: ScreenDesignInstance): string {
  const w = window as unknown as { viewer?: { get?: unknown }; __wfSelected?: { id?: string; userInUse?: string } };
  let selected: { id?: string; userInUse?: string } | undefined;
  try {
    selected = (sdInstance ? getValues(sdInstance)?.selectedWf : undefined) ?? w.__wfSelected;
  } catch {
    selected = w.__wfSelected;
  }
  if (!selected?.id) {
    return (
      " DIAGNOSIS: no workflow is currently selected in the designer, so there is no canvas to build — " +
      "re-sending this script alone will NOT help. Open the target workflow first " +
      "(ui_project_extended.navigate_to_workflow, or create_workflow), then re-send."
    );
  }
  const viewerOnly: boolean = !!w.viewer && typeof w.viewer.get === "function";
  if (viewerOnly) {
    const holder: string = selected.userInUse ? ` by [${selected.userInUse}]` : "";
    return (
      ` DIAGNOSIS: workflow "${selected.id}" is open READ-ONLY (locked${holder}), so the canvas is a viewer, ` +
      "not an editor. Editing is impossible until the lock is released — do NOT retry this script; report the lock."
    );
  }

  // The canvas BUILD FAILED (KOA-18136). Third permanent condition that used to be
  // indistinguishable from a race: `WorkFlowWindow.componentDidMount` builds inside an
  // async lifecycle, so a throw there was an unhandled rejection with no user-visible
  // trace — the modeler never appeared and identical re-sends could never fix it.
  const failure: IModelerBuildFailure | undefined = readModelerBuildFailure();
  if (failure && (!failure.workflowId || failure.workflowId === selected.id)) {
    return (
      ` DIAGNOSIS: the BPMN canvas FAILED TO BUILD for workflow "${selected.id}" — ${failure.reason}. ` +
      "This is not a mount race; re-sending the same script will NOT help. Report this error verbatim."
    );
  }

  // A workflow can be selected while the designer sits on a DIFFERENT tool (screens,
  // data model, …) — WorkFlowWindow is then unmounted and `resetModeler` has already
  // deleted the global, so nothing will ever build until the flows view is reopened.
  try {
    const dashboardState: unknown = sdInstance ? getValues(sdInstance)?.dashboardState : undefined;
    if (dashboardState !== undefined && dashboardState !== DashboardState.workFlow) {
      return (
        ` DIAGNOSIS: workflow "${selected.id}" is selected but the designer is NOT on the process-automation ` +
        "view, so the BPMN canvas is unmounted and cannot build. Open the workflow view " +
        "(ui_project_extended.navigate_to_workflow) before re-sending."
      );
    }
  } catch {
    /* diagnosis is best-effort */
  }

  return "";
}

/**
 * Repair the ONE modeler absence this executor can fix by itself: the canvas is
 * unmounted because the designer sits on a different tool (KOA-18136).
 *
 * A workflow can be SELECTED while `dashboardState` is `design` / `dataDiagram` /
 * … — WorkFlowWindow is then unmounted and `resetModeler` has already deleted the
 * global, so no amount of waiting will ever produce `window.bpmnModeler`. Left
 * alone, the script burns the full mount ceiling and atomic-aborts with a hint,
 * and the turn only recovers if the model reads that hint and calls
 * `navigate_to_workflow` — which the live KOA-18136 trace shows happening on a
 * frontier model but is exactly the "works only on frontier" shape the harness
 * bar forbids. The observed trigger is `create_workflow` reporting `opened: true`
 * without the view having landed, but any caller that reaches the DSL from
 * another view hits the same wall.
 *
 * Deliberately narrow. It runs BEFORE the wait (a canvas that cannot build must
 * not consume the mount budget first) and only when every cheap check agrees the
 * cause is a wrong view:
 *  - modeler already up → nothing to do (the healthy path pays one predicate);
 *  - nothing selected → `navigateToWorkflowView` has no target, and inventing one
 *    would open a workflow the caller never asked for;
 *  - read-only lock (`window.viewer`) or a recorded build failure → re-navigating
 *    rebuilds the same viewer / rethrows the same error, so the abort's diagnosis
 *    is the honest answer;
 *  - `dashboardState` already `workFlow` → navigating is not just useless but
 *    HARMFUL: `setDashboardState` is a toggle that would flip back to `design`
 *    and unmount a canvas that was merely mid-mount (KOA-17362).
 * `navigateToWorkflowView` carries the same not-already-there guard, so this is
 * belt-and-braces on a state read that is live (`getValues` rebuilds from the
 * providers' React state on every call). Never throws — a failed repair must
 * degrade to the existing diagnosis, not replace it with a stack trace.
 */
async function repairUnmountedWorkflowCanvas(sdInstance: ScreenDesignInstance): Promise<void> {
  try {
    if (isBpmnModelerReady()) return;

    const w = window as unknown as { viewer?: { get?: unknown } };
    if (w.viewer && typeof w.viewer.get === "function") return;

    const values = getValues(sdInstance);
    const selectedId: string | undefined = values?.selectedWf?.id ? String(values.selectedWf.id) : undefined;
    if (!selectedId) return;

    const failure: IModelerBuildFailure | undefined = readModelerBuildFailure();
    if (failure && (!failure.workflowId || failure.workflowId === selectedId)) return;

    if (values?.dashboardState === undefined || values.dashboardState === DashboardState.workFlow) return;

    await navigateToWorkflowView(sdInstance, { selectLastOpenWorkflow: false, createWhenEmpty: false });
  } catch {
    /* recovery is best-effort — the abort path still reports the real cause */
  }
}

/** The workflow-provider surface the persist points need. */
interface IWorkflowPersistProvider {
  saveWorkFlow?: (wf: unknown, changeReason?: number) => Promise<boolean> | boolean;
  /** KOA-18136 §32 — canvas → model reconciliation; see {@link persistWorkflow}. */
  quickSyncBeforeSave?: () => void;
}

/**
 * Persist the open workflow — AWAITED, and only at deliberate points (KOA-18136).
 *
 * Persistence used to be a SIDE EFFECT of the per-command handlers: every PARAM_BO /
 * PARAM_INTERNAL / PHASE / PHASE_ASSIGN / UPDATE_PARAM / DELETE_PARAM fired an
 * un-awaited `saveWorkFlow`, and `forceSyncAndFindNode` fired one more on nearly every
 * node lookup. One script therefore issued DOZENS of overlapping whole-blob PUTs of the
 * SAME KWorkFlow row. That was merely wasteful until KOA-18100 gave `KWorkFlow` a
 * `[Timestamp] RowVersion`: a stale-token write now answers `KConflictException` (409)
 * instead of silently last-write-wins, so the storm turned into a 409 storm that
 * collapsed the tail of every process build.
 *
 * Two persist points replace it, both awaited so the next step reads a settled row:
 *   1. BEFORE the first ASSIGN_SCREEN — the backend reads the PERSISTED workflow +
 *      project when it generates each managed task screen (see SCREEN_BUTTONS in
 *      `orderDslCommandsForExecution`), so unsaved params/phases would be invisible to it.
 *   2. AFTER the script — the durable write for everything the script built.
 *
 * Best-effort by design: a failed persist must not turn an otherwise-applied script into
 * a hard error (the caller already reports per-command results), and the end-of-script
 * point retries what point 1 could not land.
 */
async function persistWorkflow(sdInstance: ScreenDesignInstance, changeReason: number): Promise<void> {
  try {
    const selectedWf: unknown = getValues(sdInstance)?.selectedWf;
    if (!selectedWf) return;
    // Same resolution order as the step handlers' `getWp` — the instance first, the
    // window singleton as fallback. Without the fallback a caller that hands us a bare
    // instance would make this a SILENT no-op, and since these are now the script's only
    // save points that would mean losing the whole build with no error anywhere.
    const wp: IWorkflowPersistProvider | undefined =
      (sdInstance as unknown as { workflowProvider?: IWorkflowPersistProvider }).workflowProvider ??
      (window as unknown as { getSdInstance?: () => { workflowProvider?: IWorkflowPersistProvider } }).getSdInstance?.()
        ?.workflowProvider;
    if (!wp || typeof wp.saveWorkFlow !== "function") return;
    // KOA-18136 §32 — WHAT we save must be the canvas we just built.
    //
    // This wrote the in-memory model verbatim, and nothing in a script-driven
    // build ever populates that model from the diagram: the only code that does
    // is bound to the process window's KEYBOARD shortcuts. So an AI-built process
    // saved a diagram full of nodes next to a model with none — the right panel
    // showed no properties for any node and the backend refused the process with
    // "'Start' / 'End' / 'Step' node does not exist" (live 2026-08-16, session
    // d199aa01). Reconciling first is what makes this a save of the built graph
    // rather than a save of whatever the model happened to hold.
    //
    // The reconcile is deliberately the LOOKUP variant: it syncs and does NOT
    // save, so it cannot re-enter this function or re-open the 409 write storm
    // (KOA-18100) that made per-command saves fatal in the first place.
    if (typeof wp.quickSyncBeforeSave === "function") wp.quickSyncBeforeSave();
    await wp.saveWorkFlow(selectedWf, changeReason);
  } catch {
    // Never fail the script on a persist hiccup — the end-of-script point retries.
  }
}

/** `ESdChangeReason.SaveWorkflow` — kept numeric to avoid a model import in this handler. */
const WORKFLOW_SAVE_CHANGE_REASON: number = 26;

/**
 * KOA-18136 §35 — WHAT A CUT-OFF SCRIPT ACTUALLY DID.
 *
 * The transport gives up at 90 seconds; the browser does not. A long script keeps
 * executing and its result list dies with the abandoned response, so the author
 * learns NOTHING about a script that may have applied every single line.
 *
 * Live session d36d78e2, the whole chain from this one blind spot: a 112-line
 * script was cut off having applied EVERYTHING. The builder could not know that,
 * so it re-sent the construction script; the re-send's first node was refused as
 * "already exists", its alias went unbound, and 31 lines collapsed behind it —
 * including the four gateway conditions. With only the default marker left to
 * move, it oscillated 21 turns, the process was never accepted, and no task
 * screen was generated. The cheapest place to break that chain is here: keep the
 * ledger on THIS side of the transport and hand it to the next call.
 *
 * A run is recorded as interrupted the moment it starts and cleared when it ends
 * normally — so the record survives precisely the case where nothing else can
 * report (no return value, no throw, just an abandoned promise).
 */
interface InterruptedRunRecord {
  /** Commands the script contained. */
  totalCommands: number;
  /** First lines of the script, so the author recognizes which one this was. */
  scriptHead: string;
  /** Live reference to the run's result list — grows as commands complete. */
  completed: CommandResult[];
}

let interruptedRun: InterruptedRunRecord | null = null;

/** Short, recognizable head of a script (KOA-18136 §35). */
function summarizeScriptHead(commands: readonly DslCommand[]): string {
  return commands
    .slice(0, 4)
    .map((cmd: DslCommand): string => `${cmd.kind}@${String(cmd.line)}`)
    .join(", ");
}

/**
 * What the previous call left unfinished, phrased for the author. Returns an
 * empty string when the previous script ended normally — the common case, and one
 * that must add nothing to the response.
 */
function describeInterruptedRun(record: InterruptedRunRecord): string {
  const done: number = record.completed.length;
  const ok: number = record.completed.filter((r: CommandResult): boolean => r.success).length;
  const failed: number = done - ok;
  const allApplied: boolean = done >= record.totalCommands;
  return (
    `\n\nPREVIOUS SCRIPT WAS CUT OFF BY THE TRANSPORT, NOT STOPPED. ` +
    `It contained ${String(record.totalCommands)} command(s) (${record.scriptHead}) and ` +
    `${String(done)} of them had finished when the reply was abandoned — ${String(ok)} succeeded, ${String(failed)} failed. ` +
    (allApplied
      ? `EVERY command had already been applied, so do NOT re-send that script or any part of it: ` +
        `re-creating a node that exists collides with it, and re-stating a flow that exists is unnecessary. ` +
        `LIST the workflow, then work only on what LINT still reports.`
      : `The commands after that point may or may not have run. LIST the workflow FIRST and compare against ` +
        `what you intended; send ONLY the lines whose effect is genuinely missing.`)
  );
}

/**
 * One script = ONE persistence unit (KOA-18136).
 *
 * The batch marker tells the step handlers that the EXECUTOR owns persistence for the
 * duration, so their standalone-tool fire-and-forget saves stand down. Without it a
 * hundred-command script issued a hundred overlapping whole-blob PUTs of the same
 * KWorkFlow row — harmless until KOA-18100's `RowVersion` turned the collisions into
 * 409s. `finally` so an early return (atomic abort) or a throw can never leave the
 * marker set and silently disable persistence for later standalone tool calls.
 */
export async function executeDsl(
  sdInstance: ScreenDesignInstance,
  commands: DslCommand[],
  parseErrors: DslParseError[],
  // Modeler-readiness ceiling. Defaults to the production 15s; tests inject a
  // tiny value to exercise the atomic-abort path without a real wall-clock wait.
  modelerReadyTimeoutMs: number = MODELER_READY_TIMEOUT_MS
): Promise<ExecutionResult> {
  beginWorkflowPersistBatch();
  // KOA-18448 — the live validator re-checks the WHOLE process on every diagram change, and that
  // pass is what made a script cost minutes: live `7cf6ee92` issued 364 canvas-changing commands
  // against 28 explicit validations. Its result reaches nobody during a script — it paints icons
  // on a canvas no one is watching — and the `LINT` command builds its own linter from the
  // definitions, so what the author and the completion verdict read is unchanged.
  beginLiveLintSuspension();
  try {
    return await executeDslScript(sdInstance, commands, parseErrors, modelerReadyTimeoutMs);
  } finally {
    endLiveLintSuspension();
    endWorkflowPersistBatch();
  }
}

async function executeDslScript(
  sdInstance: ScreenDesignInstance,
  commands: DslCommand[],
  parseErrors: DslParseError[],
  modelerReadyTimeoutMs: number
): Promise<ExecutionResult> {
  const aliasMap: Record<string, string> = {};
  const results: CommandResult[] = [];
  let hasFailure = false;
  /** KOA-18136 §32 — wall clock for the cost ledger printed at the end. */
  const scriptStartedAt: number = performance.now();

  // §35 — take over the previous run's ledger (if it never finished) and open
  // this run's, pre-marked as interrupted. `results` is shared by reference, so
  // the record stays current without any bookkeeping in the command loop.
  // §37 — a fresh diagnostics window: a cause from a previous script must never be
  // reported against this one.
  clearCanvasIncidents();

  const carriedOver: InterruptedRunRecord | null = interruptedRun;
  interruptedRun = {
    totalCommands: commands.length,
    scriptHead: summarizeScriptHead(commands),
    completed: results
  };

  /**
   * KOA-18431 — task-screen composition is held OUT of the command loop.
   *
   * Its targets do not exist while the loop runs: `ASSIGN_SCREEN` is what generates the
   * managed screens, server side, and the component ids these lines address come into being
   * only then. They are collected here and replayed afterwards, per step, in one
   * open-compose-send cycle; their results carry their own line numbers, so the final sort
   * puts them back where the author wrote them.
   *
   * Partitioned BEFORE the modeler gates on purpose: these lines touch screens and never the
   * diagram, so a script made only of them must neither wait for a BPMN canvas nor restore a
   * Start/End event on whatever diagram happens to be open.
   */
  const compositionCommands: ScreenCompositionCommand[] = commands.filter(isScreenCompositionCommand);
  const loopCommands: DslCommand[] =
    compositionCommands.length === 0
      ? commands
      : commands.filter((candidate: DslCommand): boolean => !isScreenCompositionCommand(candidate));

  // KOA-17362 — modeler-mount race. When this script contains ANY command that
  // reads or mutates the diagram (everything except pure parameter authoring),
  // the live BPMN modeler MUST be present or every such command fails "BPMN
  // Modeler is not ready". A chat-driven create_workflow switches the view + sets
  // selectedWf, but the WorkFlowWindow mount that builds `window.bpmnModeler` is
  // async and a script dispatched right after races it. Wait for the modeler to
  // finish mounting before the first graph command. Param-only scripts (PARAM_BO
  // / PARAM_INTERNAL / UPDATE_PARAM / DELETE_PARAM) do not need the modeler, so
  // they skip the wait entirely (no delay when no workflow is open).
  if (scriptNeedsModeler(loopCommands)) {
    // Fix the wrong-view case FIRST — waiting on an unmounted canvas can only
    // ever time out, and spending the ceiling before the repair would double the
    // cost of the very failure this is here to remove (KOA-18136).
    await repairUnmountedWorkflowCanvas(sdInstance);
    const modelerReady: boolean = await waitForBpmnModelerReady(modelerReadyTimeoutMs);
    if (!modelerReady) {
      // Name the CAUSE. A permanent condition (nothing selected / read-only lock)
      // is indistinguishable from a mount race at the probe, and the generic
      // "retry unchanged" advice burns the turn against it. (KOA-17362)
      const diagnosis: string = describeModelerAbsence(sdInstance);
      // ATOMIC ABORT (KOA-17362). The modeler never mounted within the ceiling.
      // Do NOT run the loop: if we did, the param-only commands (PARAM_BO /
      // PARAM_INTERNAL) would succeed while every graph command failed "not
      // ready" — a HALF-APPLIED state that poisons the retry (the params now
      // "already exist", so the LLM re-authors a DIFFERENT, usually reduced graph
      // and ships an incomplete process — the observed WarehouseDecision-dropped
      // failure). Instead execute NOTHING and return one retryable signal so the
      // SAME script can be re-sent unchanged once the mount finishes.
      // §35 — nothing ran, so this run leaves no unfinished work behind. The
      // PREVIOUS run's record is preserved: the author still needs to know about it.
      interruptedRun = carriedOver;
      return {
        success: false,
        taskComplete: false,
        parseErrors,
        results: [
          {
            line: 0,
            command: "(modeler)",
            success: false,
            message:
              "BPMN Modeler is not ready yet — NO commands were executed (atomic abort; nothing was partially applied). The workflow canvas is still mounting. Re-send the IDENTICAL script UNCHANGED; do NOT re-author, reduce, or drop any step — the same script will apply cleanly once the mount completes." +
              diagnosis
          }
        ],
        aliasMap: {},
        summary: `BPMN Modeler not ready — nothing executed. Retry the exact same script unchanged.${diagnosis}`,
        preconditionUnmet: true
      };
    }
  }

  // A kuika workflow MUST have a Start and an End event. If the open workflow
  // lost one (e.g. the user deleted the End), a build/repair script that wires
  // to EndEvent_1 would fail ("Source or Target node not found") and the
  // workflow could never be fixed via the DSL. Restore the mandatory events for
  // any structural script BEFORE alias preloading, so EndEvent_1/StartEvent_1
  // resolve to a real element. Pure read-only scripts (LIST/LINT/LAYOUT) keep
  // the true broken state visible so the missing-End error still surfaces.
  if (scriptHasStructuralCommand(loopCommands)) {
    try {
      workflowEnsureStartEndEventsAction(sdInstance);
    } catch {
      // Best-effort — preload below still maps whatever events exist.
    }
  }

  // Pre-map well-known BPMN IDs (StartEvent_1, EndEvent_1) to real IDs
  preloadWellKnownIds(aliasMap);

  // Track IDs the LLM is permitted to reference. Starts with well-known
  // anchors; populated on every successful LIST and every create. Used by
  // resolve() to reject IDs copied from a prior conversation.
  const ctx: ExecCtx = {
    aliasMap,
    verifiedIds: new Set<string>(["StartEvent_1", "EndEvent_1"]),
    hasListed: false,
    pathEdges: new Map()
  };
  // Seed verifiedIds with the real IDs that StartEvent_1 / EndEvent_1 map to.
  for (const realId of Object.values(aliasMap)) {
    if (realId) ctx.verifiedIds.add(realId);
  }

  /** Small wait to let bpmn.js setTimeout callbacks complete. */
  const settle = () => new Promise<void>((r) => setTimeout(r, SETTLE_MS));

  /**
   * KOA-18136 §32 — where the script's 90-second budget actually goes.
   *
   * A 125-line build script hit the transport deadline and the recovery attempt
   * made it worse (live 2026-08-16, session d199aa01). Before tuning anything we
   * had no per-command cost at all — only the whole call's duration — so every
   * theory about WHICH command is expensive was a guess. This ledger is the
   * measurement: total wall time per command KIND, the settle tax paid, and the
   * slowest single line. It costs two clock reads per command and is printed
   * once at the end of the script, so it can stay on permanently.
   */
  const costByKind: Map<string, { calls: number; ms: number }> = new Map();
  let settleTaxMs: number = 0;
  let slowest: { kind: string; line: number; ms: number } = { kind: "-", line: 0, ms: 0 };
  const chargeCost = (kind: string, line: number, ms: number): void => {
    const entry = costByKind.get(kind) ?? { calls: 0, ms: 0 };
    entry.calls += 1;
    entry.ms += ms;
    costByKind.set(kind, entry);
    if (ms > slowest.ms) slowest = { kind, line, ms };
  };
  const reportCost = (totalMs: number): DslCostLedger => {
    const ordered: DslCostRow[] = [...costByKind.entries()]
      .sort((a, b) => b[1].ms - a[1].ms)
      .map(([kind, v]): DslCostRow => ({ kind, calls: v.calls, ms: Math.round(v.ms) }));
    console.log(
      `[DSL-COST] total=${Math.round(totalMs)}ms settleTax=${Math.round(settleTaxMs)}ms ` +
        `slowest=${slowest.kind}@line${slowest.line}(${Math.round(slowest.ms)}ms) | ` +
        ordered.map((r: DslCostRow): string => `${r.kind}=${r.ms}ms/${r.calls}`).join(" ")
    );
    // KOA-18448 — the same ledger, now also as a value so it can leave the browser. Capped at the
    // six costliest kinds: the tail is noise and this rides on every answer.
    return {
      totalMs: Math.round(totalMs),
      settleTaxMs: Math.round(settleTaxMs),
      slowest: { kind: slowest.kind, line: slowest.line, ms: Math.round(slowest.ms) },
      byKind: ordered.slice(0, 6)
    };
  };

  // Track flows we explicitly created via PATH/CONNECT, so we can detect if
  // any of them silently disappears between commands.
  interface TrackedFlow {
    id: string;
    source: string;
    target: string;
    origin: string;
  }
  const trackedFlows: TrackedFlow[] = [];
  const missingReported = new Set<string>();

  // Track PATH commands with conditions so we can re-apply them before LINT.
  // Condition state can be lost between sequential PATH commands because the
  // canvas→side-car resync (`quickSyncBeforeSave`, run by every node lookup)
  // clobbers in-memory decisions arrays. The symptom: LINT reports "condition
  // missing" on a flow that was created with a WHEN clause. Reapplying right
  // before LINT is a robust safeguard.
  interface ConditionedPath {
    pathId: string;
    conditions: ConditionEntry[];
    isOrGroup?: boolean;
  }
  const conditionedPaths: ConditionedPath[] = [];

  const auditFlows = (phase: string): void => {
    try {
      const modeler = (window as unknown as { bpmnModeler?: { get: (n: string) => unknown } }).bpmnModeler;
      if (!modeler || typeof modeler.get !== "function") return;
      const elementRegistry = modeler.get("elementRegistry") as {
        get: (id: string) => { source?: { id: string }; target?: { id: string } } | undefined;
      };
      if (!elementRegistry) return;
      for (const tf of trackedFlows) {
        if (missingReported.has(tf.id)) continue;
        const el = elementRegistry.get(tf.id);
        if (!el) {
          console.error(
            `[DSL-AUDIT ${phase}] FLOW MISSING: ${tf.id} (${tf.source} -> ${tf.target}) originally from ${tf.origin}`
          );
          missingReported.add(tf.id);
        } else {
          const curSrc = el.source?.id;
          const curTgt = el.target?.id;
          if (curSrc !== tf.source || curTgt !== tf.target) {
            console.error(
              `[DSL-AUDIT ${phase}] FLOW REWIRED: ${tf.id} originally ${tf.source} -> ${tf.target} (${tf.origin}), now ${curSrc} -> ${curTgt}`
            );
            missingReported.add(tf.id);
          }
        }
      }
    } catch {
      /* ignore */
    }
  };

  const trackFlow = (result: CommandResult, cmd: DslCommand): void => {
    if (!result.success || !result.id) return;
    if (cmd.kind === "PATH") {
      trackedFlows.push({
        id: result.id,
        source: resolve(cmd.sourceNodeId, aliasMap, ctx),
        target: resolve(cmd.targetNodeId, aliasMap, ctx),
        origin: `PATH line ${cmd.line}`
      });
    } else if (cmd.kind === "CONNECT") {
      const srcId = resolve(cmd.sourceNodeId, aliasMap, ctx);
      const tgtId = resolve(cmd.targetNodeId, aliasMap, ctx);
      // CONNECT extracts ID from "Connected with ID: X" or "Already connected with ID: X"
      const match = result.message?.match(/(?:Connected|Already connected) with ID:\s*(\S+)/);
      const flowId = match?.[1] || result.id;
      if (flowId) {
        trackedFlows.push({
          id: flowId,
          source: srcId,
          target: tgtId,
          origin: `CONNECT line ${cmd.line}`
        });
      }
    }
  };

  const reinforceConditions = async (): Promise<void> => {
    if (conditionedPaths.length === 0) return;
    // Dynamic import via require — this file's action import list already
    // includes workflowInitializePathConditionAction so it's safe.
    for (const cp of conditionedPaths) {
      try {
        workflowInitializePathConditionAction(sdInstance, {
          pathId: cp.pathId,
          conditions: cp.conditions,
          isOrGroup: cp.isOrGroup
        });
      } catch {
        // Swallow — reinforcement is best-effort
      }
    }
    await settle();
  };

  // KOA-17362 §7.6 Eksen 1 — task-screen field config is collected from the WHOLE
  // script BEFORE execution, not as the loop walks past each line. ASSIGN_SCREEN
  // generates the managed screen in one shot, so its field config must already be
  // known when it runs — and requiring the author to put every SCREEN_FIELD line
  // ABOVE the ASSIGN_SCREEN would be exactly the kind of ordering rule an LLM
  // silently gets wrong (the INTERACTION/MAP_OUTPUT lesson). A pre-pass makes the
  // line order irrelevant. Lines that no ASSIGN_SCREEN consumes are reported as a
  // fixable error after the loop — never silently dropped.
  // KOA-18136 C2 — one-shot heal for a stale browser action catalog, shared by
  // every STEP_ACTION / PRE_ACTION in this script (see createActionCatalogHealer).
  const catalogHealer: ActionCatalogHealer = createActionCatalogHealer({
    canResolve: (actionName: string): boolean => resolveWorkflowAction(sdInstance, actionName).ok,
    reload: (): Promise<void> => reloadActionDefinitions(sdInstance)
  });

  const screenFieldPlan: ScreenFieldPlan = {
    configs: commands.filter((c: DslCommand): c is ScreenFieldCmd => c.kind === "SCREEN_FIELD"),
    sections: commands.filter((c: DslCommand): c is ScreenSectionCmd => c.kind === "SCREEN_SECTION"),
    forms: commands.filter((c: DslCommand): c is ScreenFormCmd => c.kind === "SCREEN_FORM"),
    consumedLines: new Set<number>()
  };

  // Deterministic execution ordering (F3 param hoist + KOA-17362 INTERACTION-
  // after-MAP_OUTPUT). See `orderDslCommandsForExecution` for the full rationale;
  // `results` is re-sorted to input-line order after the loop so the reported
  // order is unchanged.
  const orderedCommands: DslCommand[] = orderDslCommandsForExecution(loopCommands);

  // Persist point 1 fires once, immediately before the FIRST ASSIGN_SCREEN.
  let persistedBeforeAssignScreen: boolean = false;

  /**
   * KOA-18136 §37 — THE CANVAS THIS SCRIPT IS BUILDING ON, AND WHAT IT HAS BUILT.
   *
   * A canvas can die or be REPLACED in the middle of a script. Live session be14b8f5:
   * from line 143 on every command failed "BPMN Modeler is not ready", the lines before
   * it that resolved steps failed "Step not found." / "Task not found.", and the
   * persisted model ended up with 8 of the 14 steps the script had created — because the
   * script kept going and then SAVED the damage. Detecting it is one registry read per
   * graph command; the response to it is to STOP, save NOTHING, and say where.
   */
  let canvasWitness: ICanvasWitness | undefined = undefined;
  let canvasStop: { line: number; command: string; verdict: ICanvasContinuityVerdict } | undefined = undefined;

  for (const cmd of orderedCommands) {
    // Look at the canvas BEFORE trusting it with a command.
    if (commandNeedsModeler(cmd.kind)) {
      const snapshot: ICanvasSnapshot = probeCanvas();
      const verdict: ICanvasContinuityVerdict = checkCanvasContinuity(canvasWitness, snapshot);
      if (verdict.continuity !== "intact") {
        const detail: string =
          verdict.missingIds.length > 0
            ? `missing: ${verdict.missingIds.slice(0, 8).join(", ")}`
            : describeCanvasReadability(snapshot.readability);
        recordCanvasIncident({
          purpose: `command:line ${cmd.line} ${cmd.kind}`,
          cause: verdict.continuity,
          detail,
          windowModelerPresent: isBpmnModelerReady(),
          elementCount: snapshot.presentIds.size
        });
        canvasStop = { line: cmd.line, command: cmd.kind, verdict };
        break;
      }
      noteCanvasReadable(snapshot.presentIds.size);
      // Repair a well-known anchor whose element no longer exists (the four dropped
      // `CONNECT … -> EndEvent_1` lines). Leaves live anchors untouched.
      preloadWellKnownIds(aliasMap);
      if (canvasWitness === undefined) canvasWitness = { rootId: snapshot.rootId, requiredIds: [], laneIds: [] };
    }

    // ASSIGN_SCREEN generates managed task screens SERVER-side, and the backend reads the
    // PERSISTED workflow + project to do it. The per-command fire-and-forget saves that
    // used to make that true were removed (KOA-18136 — they became a 409 storm once
    // KWorkFlow gained a RowVersion); this awaited save is their deliberate replacement.
    if (cmd.kind === "ASSIGN_SCREEN" && !persistedBeforeAssignScreen) {
      persistedBeforeAssignScreen = true;
      await persistWorkflow(sdInstance, WORKFLOW_SAVE_CHANGE_REASON);
    }

    // Before every LINT command, re-apply all path conditions. Condition
    // state can drift between PATH creation and LINT time (observed on
    // IGATEWAY paths where the side-car resync fires during subsequent
    // commands and clears the injected decision entries). Reapplying is
    // idempotent — safe to run even if no drift occurred.
    if (cmd.kind === "LINT") {
      await reinforceConditions();
    }

    let commandSucceeded: boolean = false;
    const commandStartedAt: number = performance.now();
    try {
      const result = await executeCommand(sdInstance, cmd, ctx, screenFieldPlan, catalogHealer);
      results.push(result);
      commandSucceeded = result.success === true;
      if (!result.success) hasFailure = true;
      trackFlow(result, cmd);

      // Grow verifiedIds as new elements come into existence or are confirmed
      // by LIST. Keeps resolve()'s stale-ID check accurate without false positives.
      if (result.success && result.id) {
        ctx.verifiedIds.add(result.id);
      }
      if (cmd.kind === "LIST" && result.success && result.data) {
        interface ListStep {
          id?: string;
          type?: string;
          sourceId?: string;
          targetId?: string;
          name?: string;
        }
        const steps = (result.data as { steps?: ListStep[] }).steps || [];
        // Index gateways so we can identify sequence flows that originate from one.
        const gatewayIds = new Set<string>();
        for (const s of steps) {
          if (s.id && s.type && s.type.includes("Gateway")) gatewayIds.add(s.id);
        }
        for (const s of steps) {
          if (s.id) ctx.verifiedIds.add(s.id);
          // Seed pathEdges from existing (gateway → target) sequence flows so
          // edit-scripts can't accidentally create a silent-merge duplicate.
          if (
            s.type === "bpmn:SequenceFlow" &&
            s.sourceId &&
            s.targetId &&
            gatewayIds.has(s.sourceId) &&
            !ctx.pathEdges.has(pathEdgeKey(s.sourceId, s.targetId))
          ) {
            ctx.pathEdges.set(pathEdgeKey(s.sourceId, s.targetId), {
              pathId: s.id || "(unknown)",
              pathName: s.name,
              line: cmd.line,
              // Seeded from the live diagram — NOT created by this script.
              preexisting: true
            });
          }
        }
        ctx.hasListed = true;
      }

      // Track this PATH's conditions for future reinforcement
      if (cmd.kind === "PATH" && result.success && result.id && cmd.conditions && cmd.conditions.length > 0) {
        conditionedPaths.push({
          pathId: result.id,
          conditions: cmd.conditions,
          isOrGroup: cmd.isOrGroup
        });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      results.push({ line: cmd.line, command: cmd.kind, success: false, message: msg });
      hasFailure = true;
    }

    chargeCost(cmd.kind, cmd.line, performance.now() - commandStartedAt);

    // After a creation command that ACTUALLY created something, give bpmn.js a
    // moment to settle. Gated on success (KOA-18136 §32): a rejected command
    // touched nothing, so the wait bought nothing — and on the live retry script
    // 26 rejected creations still paid it.
    if (commandSucceeded && isCreationCommand(cmd)) {
      const settleStartedAt: number = performance.now();
      await settle();
      settleTaxMs += performance.now() - settleStartedAt;
    }

    // Audit tracked flows after every command to pinpoint where a flow vanishes
    auditFlows(`after line ${cmd.line} (${cmd.kind})`);

    // §37 — keep the witness current: remember the nodes this script created, and
    // forgive what a SANCTIONED shrink removed. The forgiveness is scoped to what the
    // command may actually remove: an explicit delete accounts for anything, LINT/LAYOUT
    // only for an empty lane. A step that vanishes across a LINT is still a loss
    // (KOA-18136 — the sweep that used to make that legitimate is gone).
    if (commandNeedsModeler(cmd.kind) && canvasWitness) {
      const created: string | undefined = commandCreatesWitnessedNode(cmd.kind)
        ? results[results.length - 1]?.id
        : undefined;
      if (created && commandSucceeded) {
        canvasWitness = {
          rootId: canvasWitness.rootId,
          requiredIds: [...canvasWitness.requiredIds, created],
          laneIds: cmd.kind === "LANE" ? [...canvasWitness.laneIds, created] : canvasWitness.laneIds
        };
      }
      const shrinkScope: CanvasShrinkScope = canvasShrinkScope(cmd.kind);
      // Probe only when there is something to forgive — the read is not free, and a
      // command that may not shrink the canvas has nothing to rebase against.
      if (shrinkScope !== "none") {
        canvasWitness = rebaseCanvasWitness(canvasWitness, probeCanvas(), shrinkScope);
      }
    }
  }

  // KOA-18136 §37 — a script that stopped on a broken canvas neither CLEANS nor SAVES.
  //
  // Both are destructive on a canvas we no longer trust: the cleanup removes flows and
  // empty lanes (on a REPLACED diagram that is somebody else's work), and the save
  // writes whatever the model now holds — which is exactly how a half-built process
  // became a PERSISTED half-built process. Stopping is only a prevention if nothing
  // after the stop touches durable state.
  let disconnectedAfterRun: readonly DisconnectedNode[] = [];
  if (canvasStop === undefined) {
    // Post-execution cleanup: duplicate/auto flows and empty lanes, plus the read of
    // whatever ended up outside the flow.
    await settle();
    auditFlows("before post-execution cleanup");
    disconnectedAfterRun = finalCleanup("post-execution");
    await settle();
    auditFlows("after post-execution cleanup");

    // Persist point 2 (KOA-18136) — the durable write for everything this script built,
    // taken AFTER the cleanup so the saved side-car matches the final canvas. This is the
    // script's ONLY guaranteed save: the per-command fire-and-forget saves that used to
    // carry persistence were removed because, once KWorkFlow gained a RowVersion, dozens
    // of overlapping PUTs of the same row became a 409 storm mid-script.
    await persistWorkflow(sdInstance, WORKFLOW_SAVE_CHANGE_REASON);
  }

  // §37 — the halt answer. Deliberately BEFORE the lint / screen-config / completion
  // machinery: none of those readings mean anything on a canvas we stopped trusting, and
  // a "0 lint errors" line next to a half-built process is how a broken build got
  // reported as finished.
  if (canvasStop !== undefined) {
    const applied: number = results.length;
    const cause: string =
      canvasStop.verdict.continuity === "unreadable"
        ? "the BPMN canvas could no longer be read (the process view was closed, unmounted, or torn down)"
        : canvasStop.verdict.continuity === "replaced"
          ? "a DIFFERENT process diagram is now mounted, so the elements built by this script are not on it"
          : `the canvas lost elements this script created (${canvasStop.verdict.missingIds.slice(0, 8).join(", ")})`;
    const halt: string =
      `STOPPED AT LINE ${canvasStop.line} (${canvasStop.command}): ${cause}. ` +
      `${applied} of ${orderedCommands.length} commands had run. NOTHING was cleaned up and NOTHING was saved ` +
      `after the failure. ` +
      `Do this: make sure the process is open in the process-automation view, read it with LIST, then send every ` +
      `line of this script whose work the process does not hold yet — a line whose work already stands is not ` +
      `sent again. Do NOT re-author the process, do NOT drop a line the process does not hold, and do NOT report ` +
      `the process as built.`;
    results.push({
      line: canvasStop.line,
      command: canvasStop.command,
      success: false,
      message: halt
    });
    results.sort((a: CommandResult, b: CommandResult): number => a.line - b.line);
    const haltCost: DslCostLedger = reportCost(performance.now() - scriptStartedAt);
    // The answer carries the full ledger, so this run no longer needs the §35 record.
    interruptedRun = null;
    console.error(`[DSL-CANVAS] ${halt}`);
    return {
      success: false,
      taskComplete: false,
      parseErrors,
      results,
      aliasMap,
      cost: haltCost,
      summary: `${halt}${carriedOver ? describeInterruptedRun(carriedOver) : ""}${describeCanvasIncidents()}`,
      canvasHalt: {
        line: canvasStop.line,
        command: canvasStop.command,
        continuity: canvasStop.verdict.continuity,
        missingIds: canvasStop.verdict.missingIds,
        appliedCommands: applied,
        totalCommands: orderedCommands.length
      }
    };
  }

  // KOA-18431 — compose the task screens, now that ASSIGN_SCREEN has created them.
  //
  // After the persist, because the assignment reads the PERSISTED workflow; and after the halt
  // check, because a canvas we stopped trusting says nothing about whether the screens are there.
  // Each step is sent on its own, so one step's refusal leaves the others saved.
  if (compositionCommands.length > 0) {
    const composed: IScreenCompositionLineResult[] = await runScreenCompositionPass(
      sdInstance,
      compositionCommands,
      (ref: string): string => resolve(ref, aliasMap, ctx)
    );
    for (const line of composed) {
      results.push({ line: line.line, command: line.command, success: line.success, message: line.message });
      if (!line.success) hasFailure = true;
    }
  }

  // KOA-17362 §7.6 Eksen 1 — HONEST FLOOR for task-screen field config. A
  // SCREEN_FIELD whose step no ASSIGN_SCREEN in this script assigned was recorded
  // and then never applied. Reporting it as a success would be a silent no-op the
  // author cannot see (and the screen would ship as the generic all-editable
  // default), so the recorded row is rewritten into a fixable error naming the step.
  interface UnappliedScreenConfig {
    readonly line: number;
    readonly command: string;
    readonly stepId: string;
    readonly what: string;
  }
  const screenConfigLines: readonly UnappliedScreenConfig[] = [
    ...screenFieldPlan.configs.map((c: ScreenFieldCmd): UnappliedScreenConfig => ({
      line: c.line,
      command: "SCREEN_FIELD",
      stepId: c.stepId,
      what: `Field config for "${c.param}"`
    })),
    ...screenFieldPlan.sections.map((c: ScreenSectionCmd): UnappliedScreenConfig => ({
      line: c.line,
      command: "SCREEN_SECTION",
      stepId: c.stepId,
      what: `Form section "${c.title}"`
    })),
    ...screenFieldPlan.forms.map((c: ScreenFormCmd): UnappliedScreenConfig => ({
      line: c.line,
      command: "SCREEN_FORM",
      stepId: c.stepId,
      what: "Form layout"
    }))
  ];
  const unappliedScreenConfigs: readonly UnappliedScreenConfig[] = screenConfigLines.filter(
    (c: UnappliedScreenConfig): boolean => !screenFieldPlan.consumedLines.has(c.line)
  );
  if (unappliedScreenConfigs.length > 0) {
    const steps: string = Array.from(new Set<string>(unappliedScreenConfigs.map((c) => c.stepId))).join(", ");
    for (const config of unappliedScreenConfigs) {
      const row: CommandResult | undefined = results.find(
        (r: CommandResult): boolean => r.line === config.line && r.command === config.command
      );
      const message: string =
        `${config.what} was NOT applied: this script never assigns a managed screen to ${config.stepId}. ` +
        `Task-screen config is applied when the screen is generated — put the SCREEN_* lines and ` +
        `\`ASSIGN_SCREEN <workflowId> ${steps}\` in the SAME script (line order does not matter).`;
      if (row) {
        row.success = false;
        row.message = message;
      } else {
        results.push({ line: config.line, command: config.command, success: false, message });
      }
      hasFailure = true;
    }
  }

  const totalOk = results.filter((r) => r.success).length;
  const totalFail = results.filter((r) => !r.success).length;

  // Detect "stale reference" pattern: multiple failed DELETE/modify commands
  // with "not found". Signals the LLM is working from an outdated snapshot.
  const notFoundFailures = results.filter(
    (r) => !r.success && typeof r.message === "string" && /not found/i.test(r.message)
  );
  const staleHint =
    notFoundFailures.length >= 3
      ? ` WARNING: ${notFoundFailures.length} commands referenced IDs that no longer exist. Your reference snapshot is stale. Before retrying, put \`LIST\` as the FIRST line of your next script and use ONLY IDs from that fresh output. Do NOT copy the delete list from a previous response.`
      : "";

  // Detect unresolved lint errors. The tricky part is communicating this to
  // the LLM without triggering "my command failed" heuristics. When the LLM
  // sees success:false + "LINT STILL SHOWS ERRORS", it often interprets the
  // remaining error as "my last change didn't take effect" and retries the
  // same command — instead of applying the hint for the DIFFERENT element
  // still failing. Frame this as forward progress on a different element.
  interface LintResult {
    totalErrors?: number;
    totalWarnings?: number;
    emptyGraph?: boolean;
    flowNodeCount?: number;
    elementsWithIssues?: { elementId?: string; issues?: { message?: string; hint?: string }[] }[];
  }
  const lintResults = results.filter((r) => r.command === "LINT" && r.success && r.data);
  const lastLint = lintResults.length > 0 ? (lintResults[lintResults.length - 1].data as LintResult) : undefined;
  const remainingErrors = lastLint?.totalErrors || 0;
  // KOA-18136 §33 — a process with zero flow nodes passes lint trivially. That
  // is the worst possible outcome, not a finished one.
  const emptyGraph: boolean = lastLint?.emptyGraph === true;

  // F3 — parameter declarations may have been hoisted ahead of their input
  // line above; restore input-line order so the reported `results` array
  // matches the script the author wrote.
  results.sort((a: CommandResult, b: CommandResult): number => a.line - b.line);

  // Collect the IDs this script tried to touch (DEFAULT/CONDITION/PATH/
  // CONNECT targets) so we can tell the LLM "the remaining error is on a
  // DIFFERENT element, not the one you just fixed".
  const touchedIds = new Set<string>();
  for (const r of results) {
    if (!r.success) continue;
    if (r.id) touchedIds.add(r.id);
  }

  let lintHint = "";
  let lintIncomplete = false;
  if (emptyGraph) {
    lintIncomplete = true;
    lintHint =
      `\n\nTHE PROCESS IS EMPTY — the diagram has 0 flow nodes, so LINT has nothing to check. ` +
      `A clean lint on an empty process is NOT a finished build. Create the nodes and their ` +
      `connections (TASK / SYSTEM / XGATEWAY + PATH / CONNECT) before reporting completion.`;
  }
  if (remainingErrors > 0) {
    lintIncomplete = true;
    const issues = lastLint?.elementsWithIssues || [];
    const remainingIds = issues.map((g) => g.elementId).filter(Boolean) as string[];
    const untouchedRemaining = remainingIds.filter((id) => !touchedIds.has(id));

    const issueDigest = issues
      .slice(0, 5)
      .map((g) => {
        const firstHint = (g.issues || [])[0]?.hint;
        const msg = (g.issues || [])
          .map((i) => i.message)
          .filter(Boolean)
          .join("; ");
        return firstHint
          ? `${g.elementId} — ${msg}\n       FIX: ${firstHint.replace(/\n\s*/g, " ")}`
          : `${g.elementId} — ${msg}`;
      })
      .join("\n   • ");

    const hasSuccessfulFixes = results.some(
      (r) => r.success && (r.command === "DEFAULT" || r.command === "CONDITION" || r.command === "PATH")
    );
    const progressLine = hasSuccessfulFixes
      ? `✅ Your fixes in this script SUCCEEDED. The remaining ${remainingErrors} error(s) are on ` +
        (untouchedRemaining.length > 0
          ? `DIFFERENT element(s) (${untouchedRemaining.join(", ")}) that also need a fix.`
          : `element(s) that still need additional changes.`)
      : `Lint shows ${remainingErrors} error(s) that still need fixing.`;

    lintHint +=
      `\n\n${progressLine}\nRemaining issues (apply each \`FIX\` in your NEXT ui_workflow_dsl call):\n   • ${issueDigest}\n` +
      `\nRules: (1) Do not retry the same command — apply the FIX hint for the REMAINING element shown above. ` +
      `(2) Do not tell the user "done / temiz / düzelttim" while errors remain. ` +
      `(3) Do not ask the user for clarification — the hints are unambiguous. ` +
      `(4) Keep iterating until LINT reports totalErrors: 0.`;
  }

  // KOA-18136 — the nodes the cleanup used to delete now speak for themselves.
  //
  // A step with no arrow into or out of it is a step the script declared and never
  // wired; until this change the sweep removed it and the build looked finished with
  // the step simply gone. It is named here so the author repairs the drawing, and
  // only when the LINT in this same script has not already named it — hearing it
  // twice reads as two different problems and invites a second, wrong fix.
  const lintNamedIds: Set<string> = new Set<string>(
    (lastLint?.elementsWithIssues || []).map((g) => g.elementId).filter((id): id is string => !!id)
  );
  const unreportedDisconnected: readonly DisconnectedNode[] = disconnectedAfterRun.filter(
    (n: DisconnectedNode): boolean => !lintNamedIds.has(n.id)
  );
  if (unreportedDisconnected.length > 0) {
    lintIncomplete = true;
    const digest: string = unreportedDisconnected
      .slice(0, 8)
      .map((n: DisconnectedNode): string => `${n.id}${n.name ? ` ("${n.name}")` : ""} — ${n.type}`)
      .join("\n   • ");
    lintHint +=
      `\n\n${unreportedDisconnected.length} node(s) are standing OUTSIDE the flow — nothing enters them and ` +
      `nothing leaves them, so the process cannot reach them:\n   • ${digest}\n` +
      `\nEach one is a step that was created but never wired. Fix it by DRAWING THE MISSING ARROW ` +
      `(\`CONNECT <source> -> <thisNode>\` and \`CONNECT <thisNode> -> <next>\`, or a \`PATH\` if it hangs off a ` +
      `gateway). Delete one ONLY if the process genuinely does not need that step — a step named in the ` +
      `specification is never the answer. Do not report the process as built while this list is non-empty.`;
  }

  const cost: DslCostLedger = reportCost(performance.now() - scriptStartedAt);

  // §35 — this run finished, so it is not the interrupted one any more.
  interruptedRun = null;
  const carriedOverNote: string = carriedOver ? describeInterruptedRun(carriedOver) : "";

  // §37 — a script can finish its commands while the canvas was blind for part of the
  // run: the model↔diagram reconcile refuses to touch anything it cannot see, so
  // whatever the canvas gained during that window is not in the saved model, and the
  // backend answers "Step not found." for it later. That is not a completed build.
  const canvasIncidents: number = readCanvasIncidentCount();
  const canvasNote: string =
    canvasIncidents === 0
      ? ""
      : `\n\nTHE CANVAS WAS NOT READABLE FOR PART OF THIS SCRIPT (${canvasIncidents} incident(s)). ` +
        `The model↔diagram sync refuses to change anything it cannot see, so parts of this build may be ` +
        `missing from the SAVED process even though the commands reported success. Do NOT report the process ` +
        `as built: make sure it is open in the process-automation view, read it with LIST, send every line of ` +
        `this script whose work the process does not hold, then check LINT again.${describeCanvasIncidents()}`;

  const summary =
    (parseErrors.length > 0
      ? `Parse errors: ${parseErrors.length}. Executed: ${totalOk} ok, ${totalFail} failed.`
      : `Executed ${totalOk} commands${totalFail > 0 ? `, ${totalFail} failed` : ""}.`) +
    staleHint +
    lintHint +
    canvasNote +
    carriedOverNote;

  return {
    // Keep success=true when commands actually ran. The LLM pattern-matches
    // success:false as "my command failed" and retries verbatim; we don't
    // want that. Instead expose `taskComplete` separately to signal that
    // lint errors remain.
    success: !hasFailure && parseErrors.length === 0,
    taskComplete: !hasFailure && parseErrors.length === 0 && !lintIncomplete && canvasIncidents === 0,
    parseErrors,
    results,
    aliasMap,
    cost,
    summary
  };
}

/**
 * How long to let bpmn.js finish its own `setTimeout` work after a command that
 * CREATED something (KOA-18136 §32). Paid only when a creation actually landed —
 * a command that failed changed nothing on the canvas, so there is nothing to
 * settle and the wait was pure tax against the script's 90-second budget.
 */
const SETTLE_MS: number = 150;

function isCreationCommand(cmd: DslCommand): boolean {
  return ["TASK", "SYSTEM", "GATEWAY", "SUBPROCESS", "LINKED", "TIMER", "BOUNDARY", "LANE", "PATH", "CONNECT"].includes(
    cmd.kind
  );
}

// ── Command dispatcher ─────────────────────────────────────────────────────

async function executeCommand(
  sd: ScreenDesignInstance,
  cmd: DslCommand,
  ctx: ExecCtx,
  screenFieldPlan: ScreenFieldPlan,
  catalogHealer: ActionCatalogHealer
): Promise<CommandResult> {
  const aliasMap = ctx.aliasMap;
  const ok = (message: string, id?: string): CommandResult => ({
    line: cmd.line,
    command: cmd.kind,
    success: true,
    message,
    id
  });

  const fail = (message: string): CommandResult => ({
    line: cmd.line,
    command: cmd.kind,
    success: false,
    message
  });

  const handleResult = (
    result: { success?: boolean; error?: string; message?: string; laneId?: string },
    alias?: string
  ): CommandResult => {
    if (result.error) return fail(result.error);
    const id = extractIdFromResult(result);
    if (alias && id) aliasMap[alias] = id;
    return ok(result.message || "OK", id);
  };

  switch (cmd.kind) {
    // ── Parameters ──────────────────────────────────────────────────────
    case "PARAM_INTERNAL": {
      const result = workflowCreateInternalParameterAction(sd, {
        processId: undefined,
        name: cmd.name,
        dataType: cmd.dataType,
        isInput: cmd.isInput,
        isOutput: cmd.isOutput,
        defaultValue: cmd.defaultValue
      });
      return handleResult(result);
    }
    case "PARAM_BO": {
      const result = workflowCreateBusinessObjectParameterAction(sd, {
        processId: undefined,
        name: cmd.name,
        dataType: cmd.dataType,
        isInput: cmd.isInput,
        isOutput: cmd.isOutput,
        defaultValue: cmd.defaultValue,
        // OBJECT parameters (KOA-15840): the catalog type is resolved by the action, which
        // is where `sd.types` lives — the parser only carries the author's spelling.
        isObject: cmd.isObject,
        objectTypeName: cmd.objectTypeName,
        isList: cmd.isList
      });
      return handleResult(result);
    }

    // ── Lanes ───────────────────────────────────────────────────────────
    case "LANE": {
      const result = workflowAddLaneAction(sd, { processId: undefined, name: cmd.name });
      const cr = handleResult(result, cmd.alias);

      // If lane was created and role info provided, assign immediately
      if (cr.success && cr.id && cmd.roleId) {
        const assignResult = workflowSetLaneAssignmentAction(sd, {
          laneId: cr.id,
          assignmentData: {
            roleId: cmd.roleId,
            roleName: cmd.roleName || "Assigned Role",
            assignmentType: cmd.assignmentType ?? 2
          }
        });
        if (assignResult.error) {
          return fail(`Lane created (${cr.id}) but assignment failed: ${assignResult.error}`);
        }
      }
      return cr;
    }

    // ── Tasks & Steps ───────────────────────────────────────────────────
    case "TASK": {
      // If no after/before specified, create isolated (no auto-wiring)
      // PATH/CONNECT commands will wire it later.
      if (!cmd.afterNodeId && !cmd.beforeNodeId) {
        const result = workflowCreateIsolatedNodeAction(sd, {
          nodeType: "UserTask",
          name: cmd.name,
          laneId: cmd.laneId ? resolve(cmd.laneId, aliasMap, ctx) : undefined
        });
        return handleResult(result, cmd.alias);
      }
      const result = workflowCreateUserTaskAction(sd, {
        taskName: cmd.name,
        afterNodeId: cmd.afterNodeId ? resolve(cmd.afterNodeId, aliasMap, ctx) : undefined,
        beforeNodeId: cmd.beforeNodeId ? resolve(cmd.beforeNodeId, aliasMap, ctx) : undefined,
        laneId: cmd.laneId ? resolve(cmd.laneId, aliasMap, ctx) : undefined
      });
      return handleResult(result, cmd.alias);
    }
    case "SYSTEM": {
      if (!cmd.afterNodeId && !cmd.beforeNodeId) {
        const result = workflowCreateIsolatedNodeAction(sd, {
          nodeType: "Task",
          name: cmd.name,
          laneId: cmd.laneId ? resolve(cmd.laneId, aliasMap, ctx) : undefined
        });
        return handleResult(result, cmd.alias);
      }
      const result = workflowCreateSystemStepAction(sd, {
        stepName: cmd.name,
        afterNodeId: cmd.afterNodeId ? resolve(cmd.afterNodeId, aliasMap, ctx) : undefined,
        beforeNodeId: cmd.beforeNodeId ? resolve(cmd.beforeNodeId, aliasMap, ctx) : undefined,
        laneId: cmd.laneId ? resolve(cmd.laneId, aliasMap, ctx) : undefined
      });
      return handleResult(result, cmd.alias);
    }

    // ── Gateways ────────────────────────────────────────────────────────
    case "GATEWAY": {
      if (!cmd.afterNodeId && !cmd.beforeNodeId) {
        const gwTypeMap: Record<string, string> = {
          Exclusive: "ExclusiveGateway",
          Inclusive: "InclusiveGateway",
          Parallel: "ParallelGateway"
        };
        const result = workflowCreateIsolatedNodeAction(sd, {
          nodeType: gwTypeMap[cmd.gatewayType] || "ExclusiveGateway",
          name: cmd.name || cmd.gatewayType,
          laneId: undefined
        });
        return handleResult(result, cmd.alias);
      }
      const result = workflowAddGatewayAction(sd, {
        gatewayType: cmd.gatewayType,
        name: cmd.name,
        afterNodeId: cmd.afterNodeId ? resolve(cmd.afterNodeId, aliasMap, ctx) : undefined,
        beforeNodeId: cmd.beforeNodeId ? resolve(cmd.beforeNodeId, aliasMap, ctx) : undefined
      });
      const cr = handleResult(result, cmd.alias);

      // Clean up auto-generated outgoing "Path 1" flows from the gateway.
      // insertBpmnNode creates these when inserting between nodes, but DSL
      // expects all gateway outgoing paths to come from explicit PATH commands.
      if (cr.success && cr.id) {
        // Wait for insertBpmnNode's setTimeout(50) to complete
        await new Promise<void>((r) => setTimeout(r, 80));
        cleanupGatewayAutoFlows(cr.id);
      }

      return cr;
    }

    // ── Subprocess & Linked ─────────────────────────────────────────────
    case "SUBPROCESS": {
      if (!cmd.afterNodeId) {
        const result = workflowCreateIsolatedNodeAction(sd, {
          nodeType: "SubProcess",
          name: cmd.name
        });
        return handleResult(result, cmd.alias);
      }
      const result = workflowAddSubprocessAction(sd, {
        processId: undefined,
        name: cmd.name,
        afterNodeId: resolve(cmd.afterNodeId, aliasMap, ctx)
      });
      return handleResult(result, cmd.alias);
    }
    case "LINKED": {
      if (!cmd.afterNodeId) {
        const result = workflowCreateIsolatedNodeAction(sd, {
          nodeType: "CallActivity",
          name: cmd.name || "Linked Process"
        });
        return handleResult(result, cmd.alias);
      }
      const result = workflowAddLinkedProcessAction(sd, {
        processId: undefined,
        afterNodeId: resolve(cmd.afterNodeId, aliasMap, ctx)
      });
      return handleResult(result, cmd.alias);
    }

    // ── Events ──────────────────────────────────────────────────────────
    case "TIMER": {
      const result = workflowAddTimerIntermediateEventAction(sd, {
        processId: undefined,
        name: cmd.name,
        afterNodeId: resolve(cmd.afterNodeId, aliasMap, ctx),
        beforeNodeId: cmd.beforeNodeId ? resolve(cmd.beforeNodeId, aliasMap, ctx) : undefined
      });
      const cr = handleResult(result, cmd.alias);

      // Apply an inline wait duration (due:2Hours) right after creation.
      // Without this the timer falls back to the workflow-sync default of
      // 1 day (workflow-sync.service.ts syncEvent), silently ignoring the
      // duration the user asked for. The timer's JSON node is materialized
      // inside workflowAddTimerIntermediateEventAction's setTimeout(50), so
      // wait for it to settle before forceSync + due assignment.
      if (cr.success && cr.id && cmd.dueValue !== undefined && cmd.dueUnit) {
        await new Promise<void>((r) => setTimeout(r, 80));
        const dueResult = workflowSetTimerEventDueDateAction(sd, {
          eventId: cr.id,
          timerConfig: { dueValue: cmd.dueValue, dueUnit: cmd.dueUnit }
        });
        if (dueResult.error) {
          return fail(`Timer Event created (${cr.id}) but setting duration failed: ${dueResult.error}`);
        }
      }
      return cr;
    }
    case "BOUNDARY": {
      const result = workflowAttachBoundaryEventAction(sd, {
        nodeId: resolve(cmd.nodeId, aliasMap, ctx),
        eventType: cmd.eventType
      });
      return handleResult(result, cmd.alias);
    }

    // ── Paths & Connections ─────────────────────────────────────────────
    case "PATH": {
      const sourceId = resolve(cmd.sourceNodeId, aliasMap, ctx);
      const targetId = resolve(cmd.targetNodeId, aliasMap, ctx);

      // Reject duplicate (gateway, target) edges. bpmn.js + our finalCleanup
      // silently collapses two PATHs with the same source+target into a single
      // flow, where the LATER PATH's name + DEFAULT marker overwrite the
      // earlier one's WHEN condition. The LLM gets success:true on both
      // commands but one condition vanishes. Reject upfront with a
      // compound-condition hint so the LLM fixes semantics, not diagram shape.
      const edgeKey = pathEdgeKey(sourceId, targetId);
      const existing = ctx.pathEdges.get(edgeKey);

      // KOA-18136 §35 — an edge that ALREADY EXISTED is not a duplicate, it is the
      // thing this line wants to correct. Live session d36d78e2: a 112-line script
      // outlived the transport deadline having applied every line; the builder
      // re-sent the branches to give them their WHEN conditions, and every one came
      // back "Duplicate PATH". The condition was never written anywhere, so four
      // gateways stayed condition-less — and with only the default marker left to
      // move, the builder oscillated for 21 turns (give the default to branch A and
      // B loses its condition; give it to B and A loses its). Apply what the line
      // BRINGS to the existing flow instead of refusing it. A second PATH created
      // by THIS script is still refused below — there the merge really would lose a
      // condition.
      if (existing && existing.preexisting) {
        const applied: string[] = [];
        if (cmd.isDefault) {
          const defResult = workflowSetDefaultPathAction(sd, { gatewayId: sourceId, pathId: existing.pathId });
          if (defResult.error)
            return fail(`Existing flow ${existing.pathId} found but DEFAULT failed: ${defResult.error}`);
          applied.push("DEFAULT");
        }
        if (cmd.conditions && cmd.conditions.length > 0) {
          const condResult = workflowInitializePathConditionAction(sd, {
            pathId: existing.pathId,
            conditions: cmd.conditions,
            isOrGroup: cmd.isOrGroup
          });
          if (condResult.error) {
            return fail(`Existing flow ${existing.pathId} found but condition failed: ${condResult.error}`);
          }
          // No need to register for the pre-LINT condition reinforcement here: the
          // command loop does that for any PATH that returns success + an id with
          // conditions on the command, which is exactly what this branch returns.
          applied.push("condition");
        }
        aliasMap[`__path_${cmd.sourceNodeId}_${cmd.targetNodeId}`] = existing.pathId;
        console.log(
          `[DSL-PATH] line ${cmd.line} reused existing pathId=${existing.pathId} source=${sourceId} ` +
            `target=${targetId} applied=${applied.length > 0 ? applied.join("+") : "nothing"}`
        );
        return ok(
          applied.length > 0
            ? `Existing flow reused with ID: ${existing.pathId} — applied ${applied.join(" + ")}.`
            : `Existing flow reused with ID: ${existing.pathId} — nothing to change.`,
          existing.pathId
        );
      }

      if (existing) {
        return fail(
          `Duplicate PATH: a flow from ${sourceId} to ${targetId} already exists ` +
            `(created on line ${existing.line}, id ${existing.pathId}` +
            (existing.pathName ? `, name "${existing.pathName}"` : "") +
            `). ` +
            `A gateway cannot have two separate outgoing flows to the same target — the DSL would silently merge them and one condition would be lost. ` +
            `To route multiple conditions to the same target, keep ONE PATH and attach a compound OR condition:\n` +
            `  PATH <gateway> -> <target> "<label>" WHEN <param> <op> <value>\n` +
            `  CONDITION ${existing.pathId} WHEN <param> <op> <value1> OR <param> <op> <value2>\n` +
            `If one of the branches is meant to be the fallback, mark it DEFAULT on a DIFFERENT target, not this one.`
        );
      }

      const pathResult = workflowCreateGatewayPathAction(sd, {
        sourceNodeId: sourceId,
        targetNodeId: targetId,
        pathName: cmd.pathName
      });

      if (pathResult.error) return fail(pathResult.error);

      const pathId = extractIdFromResult(pathResult);
      console.log(
        `[DSL-PATH] line ${cmd.line} created pathId=${pathId} source=${sourceId} target=${targetId} name="${cmd.pathName || ""}" isDefault=${!!cmd.isDefault} hasConditions=${!!(cmd.conditions && cmd.conditions.length)}`
      );

      // If DEFAULT flag set, also set it
      if (cmd.isDefault && pathId) {
        workflowSetDefaultPathAction(sd, { gatewayId: sourceId, pathId });
      }

      // If conditions present, initialize them
      if (cmd.conditions && cmd.conditions.length > 0 && pathId) {
        const condResult = workflowInitializePathConditionAction(sd, {
          pathId,
          conditions: cmd.conditions,
          isOrGroup: cmd.isOrGroup
        });
        if (condResult.error) {
          return fail(`Path created (${pathId}) but condition failed: ${condResult.error}`);
        }
      }

      // Store pathId in alias map under "pathFrom_<source>_to_<target>" for later CONDITION/DEFAULT refs
      if (pathId) {
        aliasMap[`__path_${cmd.sourceNodeId}_${cmd.targetNodeId}`] = pathId;
        // Record this edge so a later PATH to the same target gets rejected upfront
        ctx.pathEdges.set(edgeKey, {
          pathId,
          pathName: cmd.pathName,
          line: cmd.line,
          preexisting: false
        });
      }

      return ok(pathResult.message || "Path created", pathId);
    }
    case "CONNECT": {
      const srcId = resolve(cmd.sourceNodeId, aliasMap, ctx);
      const tgtId = resolve(cmd.targetNodeId, aliasMap, ctx);
      const result = workflowDirectConnectAction(sd, {
        sourceNodeId: srcId,
        targetNodeId: tgtId
      });
      console.log(
        `[DSL-CONNECT] line ${cmd.line} source=${srcId} target=${tgtId} result="${result.message || result.error || ""}"`
      );
      return handleResult(result);
    }

    // ── Conditions & Defaults ───────────────────────────────────────────
    case "CONDITION": {
      const result = workflowInitializePathConditionAction(sd, {
        pathId: resolve(cmd.pathId, aliasMap, ctx),
        conditions: cmd.conditions,
        isOrGroup: cmd.isOrGroup
      });
      return handleResult(result);
    }
    case "DEFAULT": {
      const result = workflowSetDefaultPathAction(sd, {
        gatewayId: resolve(cmd.gatewayId, aliasMap, ctx),
        pathId: resolve(cmd.pathId, aliasMap, ctx),
        pathName: cmd.pathName
      });
      return handleResult(result);
    }

    // ── Rename & Delete ─────────────────────────────────────────────────
    case "RENAME": {
      const id = resolve(cmd.elementId, aliasMap, ctx);
      // Try user task rename first, fall back to system step rename
      const r1 = workflowUpdateUserTaskNameAction(sd, { taskId: id, newName: cmd.newName });
      if (!r1.error) return handleResult(r1);
      const r2 = workflowUpdateSystemStepNameAction(sd, { stepId: id, newName: cmd.newName });
      return handleResult(r2);
    }
    case "DESCRIBE": {
      const result = workflowUpdateStepPropertiesAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        description: cmd.description
      });
      return handleResult(result);
    }
    case "DELETE": {
      const deletedId = resolve(cmd.elementId, aliasMap, ctx);
      const result = workflowDeleteElementAction(sd, {
        elementId: deletedId
      });
      const cr = handleResult(result);
      // Clean up pathEdges tracking on successful delete — whether the deleted
      // element is the flow itself, or a gateway/task whose connected flows
      // were cascade-removed. Without this, a later legitimate PATH to the
      // same target would be wrongly rejected as a duplicate.
      if (cr.success) {
        for (const [key, edge] of ctx.pathEdges) {
          if (edge.pathId === deletedId || key.startsWith(`${deletedId}->`) || key.endsWith(`->${deletedId}`)) {
            ctx.pathEdges.delete(key);
          }
        }
      }
      return cr;
    }

    // ── Multi-Instance ──────────────────────────────────────────────────
    case "MI": {
      const result = workflowConfigureMultiInstanceAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        mode: cmd.mode,
        instanceCount: cmd.instanceCount,
        collectionSource: cmd.collectionSource,
        expansionMode: cmd.expansionMode,
        ordering: cmd.ordering
      });
      return handleResult(result);
    }
    case "MI_CANCEL": {
      const result = workflowInitializeMiCancelConditionAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        conditions: cmd.conditions,
        isOrGroup: cmd.isOrGroup
      });
      return handleResult(result);
    }
    case "MI_AGG": {
      const result = workflowConfigureMiAggregationAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        childOutputKey: cmd.childOutputKey,
        strategy: cmd.strategy
      });
      return handleResult(result);
    }

    // ── Boundary Event Configuration ────────────────────────────────────
    case "CONFIGURE_BOUNDARY": {
      const timerDuration =
        cmd.dueValue !== undefined && cmd.dueUnit ? { dueValue: cmd.dueValue, dueUnit: cmd.dueUnit } : undefined;
      const result = workflowConfigureBoundaryEventAction(sd, {
        eventId: resolve(cmd.eventId, aliasMap, ctx),
        isInterrupting: cmd.isInterrupting,
        timerDuration
      });
      return handleResult(result);
    }

    // ── Timer & Task Due Dates ──────────────────────────────────────────
    case "TIMER_DUE": {
      const result = workflowSetTimerEventDueDateAction(sd, {
        eventId: resolve(cmd.eventId, aliasMap, ctx),
        timerConfig: { dueValue: cmd.dueValue, dueUnit: cmd.dueUnit }
      });
      return handleResult(result);
    }
    case "TASK_DUE": {
      const result = workflowSetUserTaskDueDateAction(sd, {
        taskId: resolve(cmd.taskId, aliasMap, ctx),
        dueDateConfig: { dueValue: cmd.dueValue, dueUnit: cmd.dueUnit }
      });
      return handleResult(result);
    }

    // ── Notifications & Interactions ────────────────────────────────────
    case "NOTIFY": {
      const result = workflowSetUserTaskNotificationPreferenceAction(sd, {
        taskId: resolve(cmd.taskId, aliasMap, ctx),
        sendNotification: cmd.sendNotification
      });
      return handleResult(result);
    }
    case "INTERACTION": {
      const result = workflowAddUserInteractionAction(sd, {
        taskId: resolve(cmd.taskId, aliasMap, ctx),
        interactionData: { label: cmd.label, setParameters: cmd.parameters }
      });
      return handleResult(result);
    }

    // ── Actions ─────────────────────────────────────────────────────────
    // KOA-18136 C2 — both attach commands resolve the action BY NAME against
    // `sd.actionDefinitions`, which is populated at project load. An action the
    // Backend Architect created earlier in this same turn is therefore
    // invisible until the catalog is reloaded, and the miss reads exactly like
    // "no such action" — which is how live smoke 21 lost its four persistence
    // steps. Heal once per script before attaching; a genuinely missing action
    // still fails closed with the resolver's own message.
    case "PRE_ACTION": {
      await catalogHealer.ensureResolvable(cmd.actionName);
      const result = workflowAddPreActionToUserTaskAction(sd, {
        taskId: resolve(cmd.taskId, aliasMap, ctx),
        actionName: cmd.actionName
      });
      return handleResult(result);
    }
    case "STEP_ACTION": {
      await catalogHealer.ensureResolvable(cmd.actionName);
      const result = workflowAddActionToSystemStepAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        actionName: cmd.actionName
      });
      return handleResult(result);
    }
    case "MAP_OUTPUT": {
      const result = workflowMapStepOutputParameterAction(sd, {
        nodeId: resolve(cmd.nodeId, aliasMap, ctx),
        parameterId: cmd.parameterId,
        sourceActionName: cmd.sourceActionName,
        sourceFieldName: cmd.sourceFieldName
      });
      return handleResult(result);
    }
    case "PHASE": {
      const result = workflowDeclareHappyPathPhaseAction(sd, { name: cmd.name });
      return handleResult(result);
    }
    case "PHASE_ASSIGN": {
      const result = workflowAssignStepHappyPathPhaseAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        phaseName: cmd.phaseName
      });
      return handleResult(result);
    }
    case "MAP_INPUT": {
      const result = workflowMapStepActionInputAction(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx),
        parameterName: cmd.parameterName,
        source: cmd.source,
        isLiteral: cmd.isLiteral
      });
      return handleResult(result);
    }
    // KOA-18593 — a user task assigned by an action that returns who gets it, and that action's inputs.
    case "ASSIGN_DYNAMIC": {
      await catalogHealer.ensureResolvable(cmd.actionName);
      const result = workflowSetDynamicAssignmentAction(sd, {
        stepId: resolve(cmd.taskId, aliasMap, ctx),
        actionName: cmd.actionName,
        returns: cmd.returns
      });
      return handleResult(result);
    }
    case "ASSIGN_INPUT": {
      const result = workflowMapDynamicAssignmentInputAction(sd, {
        stepId: resolve(cmd.taskId, aliasMap, ctx),
        parameterName: cmd.parameterName,
        source: cmd.source,
        isLiteral: cmd.isLiteral
      });
      return handleResult(result);
    }

    // ── Parameter CRUD ──────────────────────────────────────────────────
    case "UPDATE_PARAM": {
      const result = workflowUpdateProcessParameterAction(sd, {
        parameterId: cmd.parameterId,
        updates: cmd.updates
      });
      return handleResult(result);
    }
    case "DELETE_PARAM": {
      const result = workflowDeleteProcessParameterAction(sd, {
        parameterId: cmd.parameterId
      });
      return handleResult(result);
    }

    // ── Lane CRUD ───────────────────────────────────────────────────────
    case "LANE_DELETE": {
      const result = workflowDeleteLaneAction(sd, {
        laneId: resolve(cmd.laneId, aliasMap, ctx)
      });
      return handleResult(result);
    }
    case "LANE_RENAME": {
      const result = workflowUpdateLaneNameAction(sd, {
        laneId: resolve(cmd.laneId, aliasMap, ctx),
        newName: cmd.newName
      });
      return handleResult(result);
    }
    case "LANE_ASSIGN": {
      const result = workflowSetLaneAssignmentAction(sd, {
        laneId: resolve(cmd.laneId, aliasMap, ctx),
        assignmentData: {
          roleId: cmd.roleId,
          roleName: cmd.roleName,
          assignmentType: cmd.assignmentType
        }
      });
      return handleResult(result);
    }

    // ── Linked Process Config ───────────────────────────────────────────
    case "LINKED_CONFIG": {
      const result = await workflowConfigureLinkedProcessParametersAction(sd, {
        linkedProcessId: resolve(cmd.linkedProcessId, aliasMap, ctx),
        targetProcessId: cmd.targetProcessId,
        parameters: cmd.parameters
      });
      return handleResult(result);
    }
    case "LINKED_RENAME": {
      const result = workflowUpdateLinkedProcessNameAction(sd, {
        linkedProcessId: resolve(cmd.linkedProcessId, aliasMap, ctx),
        newName: cmd.newName
      });
      return handleResult(result);
    }
    case "LINKED_DELETE": {
      const result = workflowDeleteLinkedProcessAction(sd, {
        linkedProcessId: resolve(cmd.linkedProcessId, aliasMap, ctx)
      });
      return handleResult(result);
    }

    // ── Subprocess CRUD ─────────────────────────────────────────────────
    case "SUBPROCESS_RENAME": {
      const result = workflowUpdateSubprocessNameAction(sd, {
        subprocessId: resolve(cmd.subprocessId, aliasMap, ctx),
        newName: cmd.newName
      });
      return handleResult(result);
    }
    case "SUBPROCESS_DELETE": {
      const result = workflowDeleteSubprocessAction(sd, {
        subprocessId: resolve(cmd.subprocessId, aliasMap, ctx)
      });
      return handleResult(result);
    }

    // ── Screen Assignment ───────────────────────────────────────────────
    // KOA-17362 §7.6 — a DECLARATION, not a mutation: the config is applied by the
    // ASSIGN_SCREEN that generates the screen. Reported as its own result line so
    // the author sees it was recorded (and, post-loop, if it was never applied).
    case "SCREEN_FIELD": {
      return ok(`Field config recorded for "${cmd.param}" on ${cmd.stepId} — applied by ASSIGN_SCREEN.`);
    }
    case "SCREEN_SECTION": {
      return ok(`Form section "${cmd.title}" recorded for ${cmd.stepId} — applied by ASSIGN_SCREEN.`);
    }
    case "SCREEN_FORM": {
      return ok(`Form layout recorded for ${cmd.stepId} — applied by ASSIGN_SCREEN.`);
    }
    case "SCREEN_BUTTONS": {
      // Applied IMMEDIATELY (a project setting), unlike the per-step SCREEN_* config:
      // the backend reads the persisted project when it generates each screen, so the
      // ordering bucket below hoists this ahead of ASSIGN_SCREEN.
      const result = await workflowSetScreenButtonLocationAction(sd, { location: cmd.location });
      return handleResult(result);
    }
    case "ASSIGN_SCREEN": {
      const resolvedStepIds = cmd.stepIds.map((s) => resolve(s, aliasMap, ctx));
      const fieldConfigs: StepFieldConfig[] = collectStepFieldConfigs(screenFieldPlan, resolvedStepIds, ctx);
      const result = await workflowAssignScreenToUserTaskAction(sd, {
        processId: cmd.processId,
        stepIds: resolvedStepIds,
        ...(fieldConfigs.length > 0 ? { fieldConfigs } : {})
      });
      if (result.error) return fail(result.error);
      const cr = ok(result.message || "OK");
      // KOA-17362 — surface the created managed task-screen ↔ step mapping in the
      // result data so the orchestrator's post-workflow enrichment phase can find
      // and design those screens. `handleResult`/`ok` otherwise drop extra fields.
      const assigned = (result as { assignedScreens?: unknown }).assignedScreens;
      if (Array.isArray(assigned)) cr.data = { assignedScreens: assigned };
      // KOA-18136 — a PARTIAL assignment is a success for the command and a failure for
      // the step it dropped. The per-step reasons ride the data alongside the successes so
      // the backstop can retry exactly the missing tasks and report WHY, instead of
      // inferring a miss from a count.
      const failedAssignments = (result as { failedAssignments?: unknown }).failedAssignments;
      if (Array.isArray(failedAssignments) && failedAssignments.length > 0) {
        cr.data = { ...(cr.data ?? {}), failedAssignments };
      }
      return cr;
    }

    // ── Path Name Update ────────────────────────────────────────────────
    case "UPDATE_PATH_NAME": {
      const result = workflowUpdatePathNameAction(sd, {
        pathId: resolve(cmd.pathId, aliasMap, ctx),
        newName: cmd.newName
      });
      return handleResult(result);
    }

    // ── Timer Event Delete ──────────────────────────────────────────────
    // (DELETE covers generic elements, but specific timer/boundary deletes exist)

    // ── Query & Layout ──────────────────────────────────────────────────
    case "SELECT": {
      const result = await selectDesignerWorkflowStep(sd, {
        stepId: resolve(cmd.stepId, aliasMap, ctx)
      });
      return handleResult(result);
    }
    case "LIST": {
      const result = listDesignerWorkflowSteps(sd, {
        workflowId: cmd.workflowId
      }) as { success?: boolean; error?: string; message?: string; steps?: unknown; count?: number };
      if (result.error) return fail(result.error);
      return {
        line: cmd.line,
        command: cmd.kind,
        success: true,
        message: result.message || `Found ${result.count || 0} elements.`,
        data: result
      };
    }
    case "LINT": {
      // Run final cleanup before linting to remove auto-generated artifacts
      finalCleanup("pre-lint");
      await new Promise<void>((r) => setTimeout(r, 100));
      const result = (await workflowGetLintIssuesAction(sd, {})) as {
        success?: boolean;
        error?: string;
        message?: string;
        totalErrors?: number;
        totalWarnings?: number;
        // KOA-18136 §37 — the "empty is never clean" evidence. §33 added these at the
        // lint site and the projection below dropped them, so the law that a 0-flow-node
        // process may not report completion has never actually run: `emptyGraph` was
        // read from a `data` object that could not carry it.
        flowNodeCount?: number;
        emptyGraph?: boolean;
        elementsWithIssues?: unknown[];
      };
      if (result.error) return fail(result.error);
      return {
        line: cmd.line,
        command: cmd.kind,
        success: true,
        message: result.message || "Lint complete.",
        data: {
          totalErrors: result.totalErrors,
          totalWarnings: result.totalWarnings,
          flowNodeCount: result.flowNodeCount,
          emptyGraph: result.emptyGraph,
          elementsWithIssues: result.elementsWithIssues
        }
      };
    }
    case "LAYOUT": {
      const result = await workflowAutoLayoutAction(sd, {});
      return handleResult(result);
    }

    // ── Task-screen composition ─────────────────────────────────────────
    // Never dispatched here: these lines are partitioned out of the loop and replayed by
    // `runScreenCompositionPass` once ASSIGN_SCREEN has created the screens they address.
    // Reaching this arm means the partition was bypassed, which would compose against a
    // screen that does not exist yet — so it fails loudly instead of quietly doing nothing.
    case "PLACE":
    case "CHILD":
    case "FIND":
    case "SET":
    case "BIND":
    case "DATA":
    case "DATAFIELD":
    case "OPTION":
    case "ACTION":
    case "ARG":
    case "ORDER":
    case "DROP": {
      return fail(
        `${cmd.kind} is a task-screen line and is applied after the script's ASSIGN_SCREEN, not in the ` +
          `command loop. Reaching the loop with it is a bug in the executor's partition.`
      );
    }

    default: {
      const _exhaustive: never = cmd;
      return fail(`Unhandled command kind: ${(cmd as DslCommand).kind}`);
    }
  }
}
